import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Ghost, Ticket, CalendarDays, ClipboardList, QrCode, RefreshCw, ExternalLink, Save, AlertTriangle, CheckCircle2, Copy, Key, Palette, Share2, Download, Mail, Send, ShieldAlert, Gift, Loader2, ImageIcon, Plus, Trash2, Clock, Play, Square as StopSquare, UserPlus, Pencil, Minus, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import HalloweenBannerConfig from './HalloweenBannerConfig';
import HHWaiversTab from './HHWaiversTab';

const API = process.env.REACT_APP_BACKEND_URL;
const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Module-scope Field component — MUST NOT be redefined inside a parent component
// (redefining it every render forces React to unmount the <input>, killing focus).
function Field({ label, value, onChange, type = 'text', placeholder, testid, prefix }) {
  return (
    <div>
      <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">{label}</label>
      <div className="flex items-center gap-2 bg-background border border-border rounded-lg px-3 py-2 focus-within:border-orange-500">
        {prefix && <span className="text-muted-foreground font-mono text-sm">{prefix}</span>}
        <input
          type={type}
          value={value ?? ''}
          placeholder={placeholder}
          data-testid={testid}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent outline-none font-mono text-sm text-foreground"
        />
      </div>
    </div>
  );
}

function SquareField({ label, value, onChange, placeholder, testid, sensitive, alreadySet }) {
  return (
    <div>
      <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block flex items-center gap-2">
        {label}
        {alreadySet && <span className="text-[9px] text-emerald-400 bg-emerald-900/30 border border-emerald-500/40 px-1.5 py-0.5 rounded normal-case">stored ✓</span>}
      </label>
      <input
        type={sensitive ? 'password' : 'text'}
        value={value ?? ''}
        placeholder={placeholder}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black border border-neutral-700 focus:border-orange-500 rounded-lg px-3 py-2 font-mono text-xs text-white outline-none"
      />
    </div>
  );
}

const SECTIONS = [
  { id: 'config',    label: 'Config & Pricing',   icon: Ticket },
  { id: 'banner',    label: 'Banner & Content',   icon: Palette },
  { id: 'calendar',  label: 'Date Capacity',      icon: CalendarDays },
  { id: 'orders',    label: 'Orders',             icon: ClipboardList },
  { id: 'comp',      label: 'Comp Passes',        icon: Gift },
  { id: 'waivers',   label: 'Waivers',            icon: ShieldAlert },
  { id: 'scanner',   label: 'Scanner',            icon: QrCode },
  { id: 'scarers',   label: 'Scarer Hours',       icon: Clock },
  { id: 'share',     label: 'Public QR',          icon: Share2 },
];

export default function HauntedHouse() {
  const [section, setSection] = useState('orders');
  const [enabled, setEnabled] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/settings/site`).then(r => setEnabled(!!r.data.halloween_enabled)).catch(() => {});
  }, []);

  const requestToggle = () => {
    if (enabled === null || toggling) return;
    setPinValue('');
    setPinError('');
    setPinOpen(true);
  };

  const confirmPinAndToggle = async () => {
    if (pinValue !== '1738') {
      setPinError('Incorrect PIN');
      setPinValue('');
      return;
    }
    setPinOpen(false);
    setToggling(true);
    const next = !enabled;
    try {
      const r = await axios.put(`${API}/api/settings/site`, { halloween_enabled: next }, { headers: authHeaders() });
      setEnabled(!!r.data.halloween_enabled);
      toast.success(next ? 'Halloween mode ON — banner + ticket shortcuts live' : 'Halloween mode OFF — site is back to normal');
    } catch (e) {
      toast.error('Could not toggle');
    } finally { setToggling(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center gap-3 mb-4">
        <Ghost className="text-orange-500" size={22} />
        <h2 className="font-heading text-2xl tracking-wide uppercase">Haunted House</h2>
        <span className="ml-2 font-heading text-[10px] tracking-widest uppercase text-orange-400 bg-orange-900/40 border border-orange-500/40 px-2 py-1 rounded">Ticketing System</span>
      </div>

      {/* MASTER ON/OFF TOGGLE — turns off banner, GIFs, jump scares, ticket entry in Book Now, everything Halloween */}
      <div
        data-testid="hh-master-toggle-card"
        className={`rounded-2xl border-2 p-5 mb-6 transition-all ${
          enabled
            ? 'border-orange-500/60 bg-gradient-to-br from-orange-950/60 via-red-950/40 to-black shadow-[0_0_28px_rgba(255,90,0,0.35)]'
            : 'border-neutral-700 bg-neutral-900/40'
        }`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${enabled ? 'bg-orange-500/20 border border-orange-500/50' : 'bg-neutral-800 border border-neutral-700'}`}>
            <Ghost size={22} className={enabled ? 'text-orange-400' : 'text-neutral-500'} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-lg tracking-widest uppercase">Halloween Mode</h3>
              <span className={`font-heading text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${enabled ? 'text-orange-300 bg-orange-900/50 border-orange-500/50' : 'text-neutral-400 bg-neutral-800 border-neutral-600'}`}>
                {enabled === null ? 'Loading…' : (enabled ? 'ON — LIVE on site' : 'OFF — site is normal')}
              </span>
            </div>
            <p className="font-sans text-xs text-muted-foreground mt-1.5">
              Master switch. When OFF, the entire site returns to standard Oki-Escape: the Halloween banner disappears, the "Rycom Mall Haunted House Ticket" entry is removed from the Book Now modal, the "Book The Fright" carnival ticket hides, and jump scares stop firing. Ticketing data (orders, dates, prices) stays saved for next year.
            </p>
          </div>
          <button
            onClick={requestToggle}
            disabled={enabled === null || toggling}
            data-testid="hh-master-toggle"
            className={`relative w-16 h-8 rounded-full transition-colors duration-200 focus:outline-none shrink-0 disabled:opacity-50 ${enabled ? 'bg-orange-500' : 'bg-neutral-700'}`}
            aria-label="Halloween master toggle"
          >
            <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${enabled ? 'translate-x-8' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>

      {pinOpen && (
        <div data-testid="hh-toggle-pin-modal"
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPinOpen(false)}>
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl border-2 border-orange-500/50 bg-gradient-to-br from-neutral-950 via-black to-orange-950/30 p-6 shadow-[0_0_40px_rgba(255,90,0,0.35)]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 border border-orange-500/50 flex items-center justify-center">
                <Ghost size={18} className="text-orange-400" />
              </div>
              <div>
                <h3 className="font-heading text-sm tracking-widest uppercase text-orange-200">Confirm Halloween Toggle</h3>
                <p className="font-sans text-[11px] text-muted-foreground">Enter the 4-digit PIN to turn Halloween mode {enabled ? 'OFF' : 'ON'}</p>
              </div>
            </div>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoFocus
              maxLength={4}
              value={pinValue}
              onChange={e => { setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') confirmPinAndToggle(); if (e.key === 'Escape') setPinOpen(false); }}
              data-testid="hh-toggle-pin-input"
              placeholder="••••"
              className="w-full bg-background border-2 border-orange-500/40 focus:border-orange-500 rounded-xl px-4 py-3 text-center font-mono text-2xl tracking-[0.5em] text-orange-200 outline-none"
            />
            {pinError && (
              <p data-testid="hh-toggle-pin-error" className="text-red-400 text-xs mt-2 text-center font-heading tracking-widest uppercase">{pinError}</p>
            )}
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button type="button" onClick={() => setPinOpen(false)}
                data-testid="hh-toggle-pin-cancel"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-muted-foreground">
                Cancel
              </button>
              <button type="button" onClick={confirmPinAndToggle}
                disabled={pinValue.length !== 4}
                data-testid="hh-toggle-pin-confirm"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-black">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-border overflow-x-auto mb-6">
        {SECTIONS.map(s => (
          <button key={s.id} data-testid={`hh-section-${s.id}`} onClick={() => setSection(s.id)}
            className={`flex items-center gap-2 font-heading text-xs tracking-widest uppercase px-3 py-2 border-b-2 transition-colors whitespace-nowrap ${section === s.id ? 'border-orange-500 text-orange-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <s.icon size={14} />{s.label}
          </button>
        ))}
      </div>

      {section === 'config' && <ConfigTab />}
      {section === 'banner' && <HalloweenBannerConfig />}
      {section === 'calendar' && <CalendarTab />}
      {section === 'orders' && <OrdersTab />}
      {section === 'comp' && <CompTab />}
      {section === 'waivers' && <HHWaiversTab />}
      {section === 'scanner' && <ScannerTab />}
      {section === 'scarers' && <ScarersTab />}
      {section === 'share' && <ShareTab />}
    </div>
  );
}

// =====================================================================
// CONFIG & PRICING
// =====================================================================
function ConfigTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/hh/settings`, { headers: authHeaders() })
      .then(r => setS(r.data))
      .catch(() => toast.error('Failed to load settings'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        weekday_price_yen: parseInt(s.weekday_price_yen) || 0,
        weekend_price_yen: parseInt(s.weekend_price_yen) || 0,
        door_pin: s.door_pin,
        event_start_date: s.event_start_date,
        event_end_date: s.event_end_date,
        default_capacity: parseInt(s.default_capacity) || 0,
        sender_email: (s.sender_email || '').trim(),
        advance_discount_type: s.advance_discount_type || 'off',
        advance_discount_value: parseInt(s.advance_discount_value) || 0,
        family_pricing_enabled: !!s.family_pricing_enabled,
        child_price_yen: parseInt(s.child_price_yen) || 0,
      };
      if ((s._resend_key_draft || '').trim()) body.resend_api_key = s._resend_key_draft.trim();
      const r = await axios.put(`${API}/api/admin/hh/settings`, body, { headers: authHeaders() });
      setS({ ...r.data, _resend_key_draft: '' });
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    const to = window.prompt('Send test email to:', s.sender_email || '');
    if (!to) return;
    try {
      await axios.post(`${API}/api/admin/hh/email/test`, { email: to }, { headers: authHeaders() });
      toast.success(`Test email sent to ${to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    }
  };

  if (!s) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <h3 className="font-heading text-sm tracking-widest uppercase text-foreground">Ticket Pricing (JPY)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Weekday Ticket (Mon–Fri)" value={s.weekday_price_yen} prefix="¥"
                 type="number" placeholder="3000" testid="hh-weekday-price"
                 onChange={v => setS({ ...s, weekday_price_yen: v })} />
          <Field label="Weekend Ticket (Sat–Sun)" value={s.weekend_price_yen} prefix="¥"
                 type="number" placeholder="4500" testid="hh-weekend-price"
                 onChange={v => setS({ ...s, weekend_price_yen: v })} />
        </div>
        <p className="text-[11px] text-muted-foreground">Weekend tickets include live actors. Amounts are whole yen — no decimals.</p>

        <div className="border-t border-border pt-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h4 className="font-heading text-xs tracking-widest uppercase text-orange-400">Advance-Purchase Discount</h4>
              <p className="text-[11px] text-muted-foreground">
                Applies to all tickets bought <b>before midnight</b> (Asia/Tokyo) of the event date. Same-day = full price.
              </p>
            </div>
            {(() => {
              const t = s.advance_discount_type;
              const v = parseInt(s.advance_discount_value) || 0;
              const wPreview = t === 'percent' ? Math.max(0, Math.floor((s.weekday_price_yen || 0) * (100 - Math.min(100, v)) / 100))
                             : t === 'yen'    ? Math.max(0, (s.weekday_price_yen || 0) - v) : (s.weekday_price_yen || 0);
              const eePreview = t === 'percent' ? Math.max(0, Math.floor((s.weekend_price_yen || 0) * (100 - Math.min(100, v)) / 100))
                              : t === 'yen'    ? Math.max(0, (s.weekend_price_yen || 0) - v) : (s.weekend_price_yen || 0);
              const active = t !== 'off' && v > 0;
              return (
                <div className={`text-[11px] font-mono px-3 py-1.5 rounded-full ${active ? 'bg-orange-950/40 border border-orange-500/50 text-orange-300' : 'bg-neutral-900 border border-neutral-700 text-neutral-500'}`}>
                  {active
                    ? `Advance: ¥${wPreview.toLocaleString()} / ¥${eePreview.toLocaleString()}`
                    : 'No discount active'}
                </div>
              );
            })()}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Discount Type</label>
              <select value={s.advance_discount_type || 'off'} data-testid="hh-advance-type"
                onChange={e => setS({ ...s, advance_discount_type: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-orange-500">
                <option value="off">Off (no discount)</option>
                <option value="percent">Percent off (%)</option>
                <option value="yen">Fixed yen off (¥)</option>
              </select>
            </div>
            <Field
              label={s.advance_discount_type === 'yen' ? 'Discount Value (¥)' : 'Discount Value (%)'}
              value={s.advance_discount_value}
              prefix={s.advance_discount_type === 'yen' ? '¥' : '%'}
              type="number"
              placeholder={s.advance_discount_type === 'yen' ? '500' : '20'}
              testid="hh-advance-value"
              onChange={v => setS({ ...s, advance_discount_value: v })}
            />
          </div>
        </div>

        {/* --- Family pricing block --- */}
        <div className="border-t border-border pt-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h4 className="font-heading text-xs tracking-widest uppercase text-orange-400">Family Pricing</h4>
              <p className="text-[11px] text-muted-foreground">
                When ON, guests pick <b>adults + kids (6–9) + kids (0–5)</b>. Kids 0–5 are free but must be accompanied by an adult.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="hh-family-toggle">
              <input type="checkbox"
                checked={!!s.family_pricing_enabled}
                onChange={e => setS({ ...s, family_pricing_enabled: e.target.checked })}
                className="sr-only peer" />
              <span className="w-11 h-6 rounded-full bg-neutral-800 border border-neutral-700 relative transition-colors peer-checked:bg-orange-600 peer-checked:border-orange-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
              <span className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">{s.family_pricing_enabled ? 'On' : 'Off'}</span>
            </label>
          </div>
          {s.family_pricing_enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="Child Ticket 6–9 yrs (¥)"
                value={s.child_price_yen}
                prefix="¥"
                type="number"
                placeholder="666"
                testid="hh-child-price"
                onChange={v => setS({ ...s, child_price_yen: v })}
              />
              <div className="rounded-lg border border-neutral-700 bg-black/40 p-3 text-[11px] text-muted-foreground">
                <div>Adult = weekday/weekend price above</div>
                <div>Kids 6–9 = <span className="text-orange-300 font-mono">¥{Number(s.child_price_yen || 666).toLocaleString()}</span> each</div>
                <div>Kids 0–5 = <span className="text-emerald-400 font-mono">FREE</span> (adult required)</div>
                <div className="mt-1 text-[10px] text-neutral-500">Advance discount applies to child tickets too.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <h3 className="font-heading text-sm tracking-widest uppercase text-foreground">Event Window & Default Capacity</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Start Date" type="date" value={s.event_start_date} testid="hh-start-date"
                 onChange={v => setS({ ...s, event_start_date: v })} />
          <Field label="End Date" type="date" value={s.event_end_date} testid="hh-end-date"
                 onChange={v => setS({ ...s, event_end_date: v })} />
          <Field label="Default Capacity / Night" type="number" value={s.default_capacity}
                 placeholder="1000" testid="hh-default-capacity"
                 onChange={v => setS({ ...s, default_capacity: v })} />
        </div>
        <p className="text-[11px] text-muted-foreground">All dates use Asia/Tokyo. Adjust individual dates in the Date Capacity tab.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <h3 className="font-heading text-sm tracking-widest uppercase text-foreground">Scanner / Door PIN</h3>
        <Field label="Door PIN" value={s.door_pin} placeholder="2026" testid="hh-door-pin"
               onChange={v => setS({ ...s, door_pin: v })} />
        <p className="text-[11px] text-muted-foreground">Staff can open <code className="text-orange-400">/scan</code> on any phone and unlock with this PIN. Admin login also works.</p>
        <a href="/scan" target="_blank" rel="noreferrer" data-testid="hh-open-scanner"
           className="inline-flex items-center gap-2 font-heading text-xs tracking-widest uppercase text-orange-500 hover:text-orange-400">
          Open Scanner <ExternalLink size={12} />
        </a>
      </div>

      <div className={`rounded-xl border p-5 space-y-4 ${s.resend_api_key_set ? 'border-emerald-700/50 bg-emerald-950/20' : 'border-orange-700/50 bg-orange-950/20'}`}>
        <div className="flex items-start gap-3">
          {s.resend_api_key_set
            ? <CheckCircle2 className="text-emerald-500 mt-0.5" size={18} />
            : <AlertTriangle className="text-orange-500 mt-0.5" size={18} />}
          <div className="flex-1 space-y-1">
            <h3 className="font-heading text-sm tracking-widest uppercase text-foreground flex items-center gap-2">
              <Mail size={14} /> Email Delivery (Resend)
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {s.resend_api_key_set
                ? 'Resend API key detected. Set a verified sender email below to email tickets to any address.'
                : 'Paste your Resend API key + sender email to enable ticket emails.'}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Sender Email (from)" value={s.sender_email}
                 placeholder="tickets@okiescape.com  or  onboarding@resend.dev"
                 testid="hh-sender-email"
                 onChange={v => setS({ ...s, sender_email: v })} />
          <Field label={s.resend_api_key_set ? 'Resend API Key (leave blank to keep)' : 'Resend API Key'}
                 value={s._resend_key_draft || ''}
                 placeholder={s.resend_api_key_set ? '•••••••• (already saved)' : 're_...'}
                 testid="hh-resend-key"
                 onChange={v => setS({ ...s, _resend_key_draft: v })} />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" onClick={sendTest} data-testid="hh-email-send-test"
            className="inline-flex items-center gap-2 text-xs font-heading tracking-widest uppercase bg-neutral-900 border border-neutral-700 hover:border-orange-500 text-white px-4 py-2 rounded-full">
            <Send size={12} /> Send Test Email
          </button>
          <p className="text-[10px] text-neutral-500 max-w-md">
            Using <code>onboarding@resend.dev</code>? Resend only lets you email your own account address until you verify a domain at
            <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-orange-400 underline mx-1">resend.com/domains</a>.
          </p>
        </div>
      </div>

      <div className={`rounded-xl border p-5 ${s.square_configured ? 'border-emerald-700/50 bg-emerald-950/20' : 'border-orange-700/50 bg-orange-950/20'}`}>
        <div className="flex items-start gap-3">
          {s.square_configured
            ? <CheckCircle2 className="text-emerald-500 mt-0.5" size={18} />
            : <AlertTriangle className="text-orange-500 mt-0.5" size={18} />}
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="font-heading text-sm tracking-widest uppercase">Square Credentials</h3>
              <select value={s.square_env || 'sandbox'} data-testid="hh-square-env-select"
                onChange={async e => {
                  const env = e.target.value;
                  try {
                    const r = await axios.put(`${API}/api/admin/hh/square-credentials`, { square_env: env }, { headers: authHeaders() });
                    setS({ ...s, ...r.data });
                    toast.success(`Switched to ${env}`);
                  } catch (err) { toast.error('Failed to switch env'); }
                }}
                className="bg-black border border-neutral-700 rounded px-2 py-1 text-xs font-mono">
                <option value="sandbox">Sandbox</option>
                <option value="production">Production (LIVE)</option>
              </select>
              {s.square_configured
                ? <span className="text-[10px] font-heading tracking-widest uppercase text-emerald-400 bg-emerald-900/30 border border-emerald-500/40 px-2 py-0.5 rounded">Connected</span>
                : <span className="text-[10px] font-heading tracking-widest uppercase text-orange-400 bg-orange-900/30 border border-orange-500/40 px-2 py-0.5 rounded">Not Configured</span>}
            </div>
            <SquareCredentialsForm current={s} onSaved={(r) => setS({ ...s, ...r })} />
            <div className="rounded-lg bg-black/50 border border-neutral-800 p-3 space-y-1">
              <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 block">Register this Webhook URL in Square</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono text-orange-300 break-all" data-testid="hh-webhook-url">{s.webhook_url || s.webhook_url_expected}</code>
                <button type="button" onClick={() => { navigator.clipboard?.writeText(s.webhook_url || s.webhook_url_expected || ''); toast.success('Copied'); }}
                  data-testid="hh-webhook-url-copy"
                  className="inline-flex items-center gap-1 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 rounded px-2 py-1 text-[11px]"><Copy size={11} />Copy</button>
              </div>
              <p className="text-[10px] text-neutral-500 mt-1">Subscribe to events: <code>payment.updated</code> and <code>refund.updated</code>.</p>
            </div>
            <p className="text-[10px] text-neutral-500">Get keys at <a target="_blank" rel="noreferrer" className="text-orange-400 underline" href="https://developer.squareup.com/apps">developer.squareup.com/apps</a>. Access tokens and signature keys are stored server-side and never returned to the browser.</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="hh-save-settings"
          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-black font-heading text-xs tracking-widest uppercase px-5 py-2.5 rounded-full disabled:opacity-50">
          <Save size={14} />{saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// CALENDAR / CAPACITY
// =====================================================================
function CalendarTab() {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {date, capacity, is_open, note_en, note_ja}

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/hh/dates`, { headers: authHeaders() });
      setDates(r.data.dates || []);
    } catch (e) { toast.error('Failed to load dates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await axios.put(`${API}/api/admin/hh/dates/${editing.date}`, {
        capacity: parseInt(editing.capacity) || 0,
        is_open: !!editing.is_open,
        note_en: editing.note_en || '',
        note_ja: editing.note_ja || '',
      }, { headers: authHeaders() });
      setEditing(null);
      await load();
      toast.success('Saved');
    } catch (e) { toast.error('Save failed'); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading dates…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Override per-night capacity. Sold + Held seats deduct from Available.</p>
        <button onClick={load} data-testid="hh-refresh-dates" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {dates.map(d => {
          const pct = d.capacity > 0 ? Math.round(((d.sold + d.held) / d.capacity) * 100) : 0;
          return (
            <button key={d.date} onClick={() => setEditing({ ...d })} data-testid={`hh-date-${d.date}`}
              className={`text-left rounded-lg border p-2 transition-colors ${d.is_open ? 'border-border hover:border-orange-500' : 'border-red-800/50 bg-red-950/20'} ${d.is_weekend ? 'ring-1 ring-orange-800/40' : ''}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading text-xs">{d.date.slice(5)}</span>
                {d.is_weekend && <span className="text-[9px] font-heading tracking-widest uppercase text-orange-400">Wknd</span>}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Cap {d.capacity}</div>
              <div className="text-[11px] mt-0.5">
                <span className="text-emerald-400">Sold {d.sold}</span>
                {d.held > 0 && <span className="text-orange-400 ml-1">•H{d.held}</span>}
              </div>
              <div className="mt-1 h-1 bg-neutral-800 rounded overflow-hidden">
                <div className={`h-full ${pct >= 90 ? 'bg-red-500' : pct >= 60 ? 'bg-orange-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              {!d.is_open && <div className="text-[9px] font-heading tracking-widest uppercase text-red-400 mt-1">Closed</div>}
            </button>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg tracking-wide">{editing.date} {editing.is_weekend ? '(Weekend)' : '(Weekday)'}</h3>
              <button onClick={() => setEditing(null)} className="text-muted-foreground">✕</button>
            </div>
            <div>
              <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Capacity</label>
              <input type="number" value={editing.capacity} data-testid="hh-edit-capacity"
                onChange={e => setEditing({ ...editing, capacity: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!editing.is_open} data-testid="hh-edit-is-open"
                onChange={e => setEditing({ ...editing, is_open: e.target.checked })} />
              Open for booking
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Note (EN)</label>
                <input value={editing.note_en || ''} onChange={e => setEditing({ ...editing, note_en: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Note (JA)</label>
                <input value={editing.note_ja || ''} onChange={e => setEditing({ ...editing, note_ja: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <div className="text-xs text-muted-foreground">Sold {editing.sold} • Held {editing.held} • Available {editing.available}</div>
              <button onClick={save} data-testid="hh-save-date"
                className="bg-orange-600 hover:bg-orange-500 text-black font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// ORDERS
// =====================================================================
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [compOnly, setCompOnly] = useState(false);
  const [detail, setDetail] = useState(null);
  const [syncing, setSyncing] = useState('');   // order_id being synced, or 'all'
  const [deleting, setDeleting] = useState('');  // order_id being deleted
  const [confirmDelete, setConfirmDelete] = useState(null); // order object awaiting confirmation
  const [reschedule, setReschedule] = useState(null); // order object being rescheduled
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleFlex, setRescheduleFlex] = useState(false);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  const [eventDates, setEventDates] = useState([]); // {date, is_open, available, ...}
  const [walkinTotals, setWalkinTotals] = useState({});
  const [walkinEntries, setWalkinEntries] = useState([]);
  const [walkinRevenue, setWalkinRevenue] = useState({});
  const [walkinAdd, setWalkinAdd] = useState(1);
  const [walkinEmployee, setWalkinEmployee] = useState('');
  const [walkinBusy, setWalkinBusy] = useState('');
  const [walkinLogOpen, setWalkinLogOpen] = useState(false);
  const [pricing, setPricing] = useState({ weekday_price_yen: 0, weekend_price_yen: 0 });
  const [editWalkin, setEditWalkin] = useState(null);
  const [walkinAdjusting, setWalkinAdjusting] = useState('');
  const [editCount, setEditCount] = useState('');
  const [editEmployee, setEditEmployee] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editType, setEditType] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const loadWalkins = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/hh/walkins`, { headers: authHeaders() });
      setWalkinTotals(r.data?.totals || {});
      setWalkinEntries(r.data?.entries || []);
      setWalkinRevenue(r.data?.revenue_by_date || {});
    } catch { /* non-fatal */ }
  };
  const loadPricing = async () => {
    try {
      const r = await axios.get(`${API}/api/hh/config`);
      setPricing({
        weekday_price_yen: Number(r.data?.weekday_price_yen || 0),
        weekend_price_yen: Number(r.data?.weekend_price_yen || 0),
      });
    } catch { /* non-fatal */ }
  };

  const openCarnival = async (ident) => {
    try {
      const r = await axios.get(`${API}/api/admin/hh/order/${ident}/carnival-pdf`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to open carnival PDF');
    }
  };

  const deleteOrder = async () => {
    const o = confirmDelete;
    if (!o) return;
    setDeleting(o.order_id);
    try {
      await axios.delete(`${API}/api/admin/hh/orders/${o.order_id}`, { headers: authHeaders() });
      toast.success(`Deleted ${o.order_num}`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(''); }
  };

  const openReschedule = (o) => {
    setReschedule(o);
    setRescheduleDate(o.event_date || '');
    setRescheduleFlex(!!o.flex_pass || !o.event_date);
    // Lazy-load the full dates list the first time
    if (eventDates.length === 0) {
      axios.get(`${API}/api/hh/config`).then(r => setEventDates(r.data?.dates || [])).catch(() => {});
    }
  };

  const submitReschedule = async () => {
    const o = reschedule;
    if (!o) return;
    if (!rescheduleFlex && !rescheduleDate) {
      toast.error('Pick a date or switch to Any Night');
      return;
    }
    setRescheduleBusy(true);
    try {
      const payload = rescheduleFlex ? { flex: true } : { event_date: rescheduleDate };
      await axios.patch(`${API}/api/admin/hh/orders/${o.order_id}/reschedule`,
        payload, { headers: authHeaders() });
      toast.success(rescheduleFlex
        ? `${o.order_num} is now Any Night (tickets reset to VALID)`
        : `${o.order_num} moved to ${rescheduleDate} (tickets reset to VALID)`);
      setReschedule(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reschedule failed');
    } finally { setRescheduleBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = filterDate ? { event_date: filterDate } : {};
      const r = await axios.get(`${API}/api/admin/hh/orders`, { headers: authHeaders(), params });
      setOrders(r.data.orders || []);
    } catch (e) { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterDate]);
  useEffect(() => { loadWalkins(); loadPricing(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const addWalkins = async (ticketType = '') => {
    const target = filterDate || todayIso;
    const n = parseInt(walkinAdd, 10);
    if (!target || !n) return;
    setWalkinBusy(ticketType || 'free');
    try {
      await axios.post(`${API}/api/admin/hh/walkins`, {
        event_date: target, count: n,
        ticket_type: ticketType || undefined,
        employee_name: walkinEmployee.trim() || undefined,
      }, { headers: authHeaders() });
      const price = ticketType === 'weekend' ? pricing.weekend_price_yen
                  : ticketType === 'weekday' ? pricing.weekday_price_yen : 0;
      const totalMsg = price > 0 ? ` · ${yen(price * n)}` : '';
      toast.success(`+${n} ${ticketType || 'walk-in'}${n === 1 ? '' : 's'} for ${target}${totalMsg}`);
      await loadWalkins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add walk-ins');
    } finally { setWalkinBusy(''); }
  };

  const openEditWalkin = (w) => {
    setEditWalkin(w);
    setEditCount(String(w.count));
    setEditEmployee(w.employee_name || '');
    setEditDate(w.event_date || '');
    setEditType(w.ticket_type || '');
  };

  const submitEditWalkin = async () => {
    if (!editWalkin) return;
    const c = parseInt(editCount, 10);
    if (!c || c === 0) {
      toast.error('Count must be a non-zero number');
      return;
    }
    setEditBusy(true);
    try {
      await axios.patch(`${API}/api/admin/hh/walkins/${editWalkin.id}`, {
        count: c,
        employee_name: editEmployee,
        event_date: editDate || editWalkin.event_date,
        ticket_type: editType || '',
      }, { headers: authHeaders() });
      toast.success('Walk-in updated');
      setEditWalkin(null);
      await loadWalkins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally { setEditBusy(false); }
  };

  const adjustWalkin = async (w, delta) => {
    const next = w.count + delta;
    if (next === 0) return deleteWalkin(w);
    setWalkinAdjusting(w.id);
    try {
      await axios.patch(`${API}/api/admin/hh/walkins/${w.id}`, { count: next }, { headers: authHeaders() });
      await loadWalkins();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally { setWalkinAdjusting(''); }
  };

  const deleteWalkin = async (w) => {
    if (!window.confirm(`Delete this walk-in entry (${w.count > 0 ? '+' : ''}${w.count} on ${w.event_date})?`)) return;
    try {
      await axios.delete(`${API}/api/admin/hh/walkins/${w.id}`, { headers: authHeaders() });
      await loadWalkins();
    } catch { toast.error('Could not delete'); }
  };

  const openDetail = async (order_id) => {
    try {
      const r = await axios.get(`${API}/api/admin/hh/orders/${order_id}`, { headers: authHeaders() });
      setDetail(r.data);
    } catch (e) { toast.error('Failed to load order'); }
  };

  const syncOne = async (order_id) => {
    setSyncing(order_id);
    try {
      const r = await axios.post(`${API}/api/admin/hh/orders/${order_id}/sync`, {}, { headers: authHeaders() });
      if (r.data?.refunded) toast.success(`${r.data.order_num}: refund detected — tickets voided`);
      else toast.success(`${r.data.order_num}: no refund on Square`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(''); }
  };

  const syncAll = async () => {
    setSyncing('all');
    try {
      const params = filterDate ? { event_date: filterDate } : {};
      const r = await axios.post(`${API}/api/admin/hh/orders/sync-all`, {}, { headers: authHeaders(), params });
      const found = r.data?.refunds_found || 0;
      const checked = r.data?.checked || 0;
      if (found === 0) toast.success(`Checked ${checked} orders — no new refunds on Square`);
      else toast.success(`Checked ${checked} orders — voided ${found} refunded ticket(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(''); }
  };

  const filtered = useMemo(() => {
    let list = filterStatus
      ? orders.filter(o => (o.computed_status || '').toUpperCase() === filterStatus)
      : orders;
    if (compOnly) list = list.filter(o => !!o.comp);
    // Order:
    //   1. Dated orders first, sorted by event_date ascending (today / Oct 1 at top)
    //   2. Flex / ANY-date comp passes at the very bottom (newest first)
    // Within same event_date, newer orders first.
    return [...list].sort((a, b) => {
      const aFlex = !!a.flex_pass || !a.event_date;
      const bFlex = !!b.flex_pass || !b.event_date;
      if (aFlex !== bFlex) return aFlex ? 1 : -1;
      if (aFlex && bFlex) {
        // Both flex — newest first
        return String(b.created_at || '').localeCompare(String(a.created_at || ''));
      }
      const dateCmp = String(a.event_date || '').localeCompare(String(b.event_date || ''));
      if (dateCmp !== 0) return dateCmp;
      return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });
  }, [orders, filterStatus, compOnly]);

  const counts = orders.reduce((acc, o) => {
    const k = (o.computed_status || 'UNKNOWN').toUpperCase();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const compCount = orders.filter(o => !!o.comp).length;

  // ---- Daily rollup: tickets sold + revenue per event_date (upcoming first) ----
  const todayIso = useMemo(() => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      const get = (k) => parts.find(p => p.type === k)?.value || '';
      return `${get('year')}-${get('month')}-${get('day')}`;
    } catch { return new Date().toISOString().slice(0, 10); }
  }, []);

  const lastWalkin = useMemo(() => {
    const target = filterDate || todayIso;
    return walkinEntries
      .filter(w => w.event_date === target)
      .reduce((a, w) => (!a || (w.added_at || '') > (a.added_at || '') ? w : a), null);
  }, [walkinEntries, filterDate, todayIso]);

  const dailyRollup = useMemo(() => {
    const map = {};
    for (const o of orders) {
      // Flex / ANY-date passes don't belong on a specific date rollup
      if (o.flex_pass || !o.event_date) continue;
      const d = o.event_date || 'unknown';
      if (!map[d]) map[d] = { date: d, tickets: 0, paidTickets: 0, revenue: 0, compTickets: 0, orders: 0, used: 0, walkins: 0 };
      const qty = Number(o.quantity || 0);
      map[d].orders += 1;
      if (o.comp) {
        map[d].compTickets += qty;
      } else {
        // Only paid tickets count toward the headline "tickets" total
        map[d].tickets += qty;
        map[d].paidTickets += qty;
        map[d].used += Number(o.ticket_used_count || 0);
        map[d].revenue += Number(o.total_yen || 0);
      }
    }
    // Also surface dates that only have manual walk-ins (no ticketed orders)
    Object.entries(walkinTotals || {}).forEach(([d, n]) => {
      if (!map[d]) map[d] = { date: d, tickets: 0, paidTickets: 0, revenue: 0, compTickets: 0, orders: 0, used: 0, walkins: 0 };
      map[d].walkins = Number(n) || 0;
    });
    const rows = Object.values(map).filter(r => r.date !== 'unknown');
    // ALWAYS surface today at the top, even if there are no orders / walk-ins yet.
    if (!rows.some(r => r.date === todayIso)) {
      rows.push({ date: todayIso, tickets: 0, paidTickets: 0, revenue: 0, compTickets: 0, orders: 0, used: 0, walkins: 0 });
    }
    // Today first, then future dates ascending, then past dates most-recent first
    const today = rows.filter(r => r.date === todayIso);
    const future = rows.filter(r => r.date > todayIso).sort((a, b) => a.date.localeCompare(b.date));
    const past = rows.filter(r => r.date < todayIso).sort((a, b) => b.date.localeCompare(a.date));
    return [...today, ...future, ...past];
  }, [orders, todayIso, walkinTotals]);

  const todayRollup = dailyRollup.find(r => r.date === todayIso);

  // ---- Monthly rollup: tickets + walk-ins per YYYY-MM ----
  const monthlyMap = useMemo(() => {
    const map = {};
    for (const o of orders) {
      if (o.flex_pass || !o.event_date) continue;
      const ym = String(o.event_date).slice(0, 7);
      if (!map[ym]) map[ym] = { ym, tickets: 0, paidTickets: 0, compTickets: 0, revenue: 0, walkinRevenue: 0, walkins: 0, orders: 0 };
      const qty = Number(o.quantity || 0);
      map[ym].orders += 1;
      if (o.comp) {
        map[ym].compTickets += qty;
      } else {
        // Only paid tickets count toward the headline "tickets" total
        map[ym].tickets += qty;
        map[ym].paidTickets += qty;
        map[ym].revenue += Number(o.total_yen || 0);
      }
    }
    Object.entries(walkinTotals || {}).forEach(([d, n]) => {
      const ym = String(d).slice(0, 7);
      if (!map[ym]) map[ym] = { ym, tickets: 0, paidTickets: 0, compTickets: 0, revenue: 0, walkinRevenue: 0, walkins: 0, orders: 0 };
      map[ym].walkins += Number(n) || 0;
    });
    Object.entries(walkinRevenue || {}).forEach(([d, yenAmt]) => {
      const ym = String(d).slice(0, 7);
      if (!map[ym]) map[ym] = { ym, tickets: 0, paidTickets: 0, compTickets: 0, revenue: 0, walkinRevenue: 0, walkins: 0, orders: 0 };
      map[ym].walkinRevenue += Number(yenAmt) || 0;
    });
    return map;
  }, [orders, walkinTotals, walkinRevenue]);

  const [monthYm, setMonthYm] = useState(() => todayIso.slice(0, 7));
  useEffect(() => { if (monthYm.length !== 7) setMonthYm(todayIso.slice(0, 7)); }, [todayIso, monthYm]);
  const monthRow = monthlyMap[monthYm] || { ym: monthYm, tickets: 0, paidTickets: 0, compTickets: 0, revenue: 0, walkinRevenue: 0, walkins: 0, orders: 0 };
  const monthLabel = (() => {
    try {
      const [y, m] = monthYm.split('-').map(Number);
      return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } catch { return monthYm; }
  })();
  const shiftMonth = (delta) => {
    const [y, m] = monthYm.split('-').map(Number);
    const d = new Date(y, (m - 1) + delta, 1);
    const ny = d.getFullYear();
    const nm = String(d.getMonth() + 1).padStart(2, '0');
    setMonthYm(`${ny}-${nm}`);
  };

  const CHIPS = ['VALID', 'PARTIAL', 'USED', 'EXPIRED', 'REFUNDED', 'PENDING'];

  return (
    <div className="space-y-4">
      {/* Today's live counter — hero banner */}
      {todayRollup && (
        <div
          data-testid="hh-today-counter"
          className="rounded-xl border-2 border-orange-500/60 bg-gradient-to-r from-orange-950/50 via-red-950/40 to-orange-950/50 p-4 flex items-center justify-between gap-4"
          style={{ boxShadow: '0 0 24px rgba(249,115,22,0.25)' }}
        >
          <div>
            <p className="font-heading text-[10px] tracking-widest uppercase text-orange-300">Today · {todayRollup.date}</p>
            <p className="font-heading text-3xl text-orange-200 mt-0.5" style={{ textShadow: '0 0 12px rgba(249,115,22,0.6)' }}>
              {todayRollup.tickets + (todayRollup.walkins || 0)} <span className="text-sm text-orange-300/80">total guests</span>
            </p>
            <p className="text-[11px] text-orange-100/70 mt-0.5">
              {todayRollup.tickets} tickets ({todayRollup.used}/{todayRollup.tickets} scanned) · {todayRollup.orders} order{todayRollup.orders === 1 ? '' : 's'}
              {todayRollup.walkins > 0 && ` · ${todayRollup.walkins} walk-in${todayRollup.walkins === 1 ? '' : 's'}`}
              {todayRollup.compTickets > 0 && ` · ${todayRollup.compTickets} comp`}
            </p>
          </div>
          <div className="text-right">
            <p className="font-heading text-[10px] tracking-widest uppercase text-emerald-300">Paid revenue</p>
            <p className="font-mono text-xl text-emerald-200 mt-0.5">{yen(todayRollup.revenue)}</p>
          </div>
        </div>
      )}

      {/* Monthly total — tickets + walk-ins + grand total */}
      <div
        data-testid="hh-month-counter"
        className="rounded-xl border-2 border-fuchsia-500/50 bg-gradient-to-r from-fuchsia-950/40 via-purple-950/30 to-fuchsia-950/40 p-4"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => shiftMonth(-1)}
              data-testid="hh-month-prev"
              className="w-7 h-7 rounded-full bg-fuchsia-900/50 hover:bg-fuchsia-800 border border-fuchsia-500/40 text-fuchsia-200 font-heading">‹</button>
            <div>
              <p className="font-heading text-[10px] tracking-widest uppercase text-fuchsia-300">Monthly Total</p>
              <p data-testid="hh-month-label" className="font-heading text-lg text-fuchsia-100 leading-tight">{monthLabel}</p>
            </div>
            <button type="button" onClick={() => shiftMonth(1)}
              data-testid="hh-month-next"
              className="w-7 h-7 rounded-full bg-fuchsia-900/50 hover:bg-fuchsia-800 border border-fuchsia-500/40 text-fuchsia-200 font-heading">›</button>
            {monthYm !== todayIso.slice(0, 7) && (
              <button type="button" onClick={() => setMonthYm(todayIso.slice(0, 7))}
                className="ml-1 font-heading text-[10px] tracking-widest uppercase text-fuchsia-300 hover:text-fuchsia-200 underline">
                this month
              </button>
            )}
          </div>
          <div className="text-right">
            <p className="font-heading text-[10px] tracking-widest uppercase text-emerald-300">Paid revenue</p>
            <p data-testid="hh-month-revenue" className="font-mono text-lg text-emerald-200 mt-0.5">{yen(monthRow.revenue)}</p>
            {monthRow.walkinRevenue > 0 && (
              <p data-testid="hh-month-walkin-revenue" className="font-mono text-[11px] text-cyan-300 mt-0.5">
                + {yen(monthRow.walkinRevenue)} walk-in revenue
              </p>
            )}
            {monthRow.walkinRevenue > 0 && (
              <p data-testid="hh-month-total-revenue" className="font-mono text-[11px] text-amber-200 mt-0.5 border-t border-emerald-500/20 pt-0.5">
                = {yen(monthRow.revenue + monthRow.walkinRevenue)} total
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="rounded-lg bg-black/40 border border-fuchsia-500/30 p-3 text-center">
            <p className="font-heading text-[9px] tracking-widest uppercase text-fuchsia-300/80">Tickets Sold</p>
            <p data-testid="hh-month-tickets" className="font-heading text-2xl text-fuchsia-100 mt-0.5">{monthRow.tickets}</p>
            <p className="text-[10px] text-fuchsia-200/60">
              paid only
              {monthRow.compTickets > 0 && (
                <span className="block text-fuchsia-300/70">+ {monthRow.compTickets} comp (not counted)</span>
              )}
            </p>
          </div>
          <div className="rounded-lg bg-black/40 border border-cyan-500/30 p-3 text-center">
            <p className="font-heading text-[9px] tracking-widest uppercase text-cyan-300/80">Walk-ins</p>
            <p data-testid="hh-month-walkins" className="font-heading text-2xl text-cyan-100 mt-0.5">{monthRow.walkins}</p>
            <p className="text-[10px] text-cyan-200/60">manual count</p>
          </div>
          <div className="rounded-lg bg-gradient-to-br from-orange-900/50 to-fuchsia-900/40 border-2 border-orange-500/50 p-3 text-center">
            <p className="font-heading text-[9px] tracking-widest uppercase text-orange-300">Grand Total</p>
            <p data-testid="hh-month-grand-total" className="font-heading text-2xl text-orange-100 mt-0.5" style={{ textShadow: '0 0 10px rgba(249,115,22,0.5)' }}>
              {monthRow.tickets + monthRow.walkins}
            </p>
            <p className="text-[10px] text-orange-200/70">all guests</p>
          </div>
        </div>
      </div>

      {/* Manual walk-in counter — type a number, tap Add */}
      <div
        data-testid="hh-walkin-widget"
        className="rounded-xl border border-border bg-card p-4 space-y-3"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <ClipboardList size={16} className="text-orange-500" />
            <div>
              <p className="font-heading text-xs tracking-widest uppercase">Add Walk-ins</p>
              <p className="text-[10px] text-muted-foreground">
                Count unticketed guests for <b className="text-orange-300">{filterDate || todayIso}</b>
              </p>
            </div>
          </div>
          <input
            type="number"
            value={walkinAdd}
            onChange={e => setWalkinAdd(e.target.value)}
            min={-50} max={200}
            data-testid="hh-walkin-input"
            className="w-20 bg-background border border-border rounded-lg px-3 py-2 font-mono text-sm text-center outline-none focus:border-orange-500"
          />
          <input
            type="text"
            value={walkinEmployee}
            onChange={e => setWalkinEmployee(e.target.value)}
            placeholder="Employee name (optional)"
            maxLength={60}
            data-testid="hh-walkin-employee"
            className="flex-1 min-w-[180px] bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-orange-500"
          />
          <button
            type="button"
            onClick={() => addWalkins('weekday')}
            disabled={walkinBusy !== '' || !parseInt(walkinAdd, 10)}
            data-testid="hh-walkin-add-weekday"
            title={`${yen(pricing.weekday_price_yen)} / walk-in`}
            className="inline-flex items-center gap-1 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-black font-heading text-[11px] tracking-widest uppercase px-3 py-2 rounded-full"
          >
            {walkinBusy === 'weekday' ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />}
            Weekday <span className="font-mono text-[10px] opacity-80 ml-1">{yen(pricing.weekday_price_yen * (parseInt(walkinAdd, 10) || 0))}</span>
          </button>
          <button
            type="button"
            onClick={() => addWalkins('weekend')}
            disabled={walkinBusy !== '' || !parseInt(walkinAdd, 10)}
            data-testid="hh-walkin-add-weekend"
            title={`${yen(pricing.weekend_price_yen)} / walk-in`}
            className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-heading text-[11px] tracking-widest uppercase px-3 py-2 rounded-full"
          >
            {walkinBusy === 'weekend' ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />}
            Weekend <span className="font-mono text-[10px] opacity-80 ml-1">{yen(pricing.weekend_price_yen * (parseInt(walkinAdd, 10) || 0))}</span>
          </button>
          <button
            type="button"
            onClick={() => addWalkins('')}
            disabled={walkinBusy !== '' || !parseInt(walkinAdd, 10)}
            data-testid="hh-walkin-add"
            title="Count only (no revenue — e.g. staff / comp walk-ins)"
            className="inline-flex items-center gap-1 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-50 text-orange-300 border border-orange-500/40 font-heading text-[10px] tracking-widest uppercase px-3 py-2 rounded-full"
          >
            {walkinBusy === 'free' ? <Loader2 className="animate-spin" size={11} /> : <Plus size={11} />}
            Count Only
          </button>
          {lastWalkin && (
            <button
              type="button"
              onClick={() => deleteWalkin(lastWalkin)}
              data-testid="hh-walkin-undo-last"
              title="Remove the most recent walk-in entry for this date"
              className="inline-flex items-center gap-1 bg-neutral-800 hover:bg-neutral-700 text-amber-300 border border-amber-500/40 font-heading text-[10px] tracking-widest uppercase px-3 py-2 rounded-full"
            >
              <Undo2 size={11} /> Undo Last ({lastWalkin.count > 0 ? '+' : ''}{lastWalkin.count})
            </button>
          )}
          <button
            type="button"
            onClick={() => setWalkinLogOpen(v => !v)}
            data-testid="hh-walkin-toggle-log"
            className="inline-flex items-center gap-1 bg-neutral-800 hover:bg-neutral-700 text-fuchsia-200 border border-fuchsia-500/30 font-heading text-[10px] tracking-widest uppercase px-3 py-2 rounded-full"
          >
            <ClipboardList size={11} /> {walkinLogOpen ? 'Hide Log' : `View Log (${walkinEntries.length})`}
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap text-[11px]">
          {(walkinTotals[filterDate || todayIso] || 0) !== 0 && (
            <span className="text-fuchsia-300 font-mono">
              {walkinTotals[filterDate || todayIso]} walk-ins · {yen(walkinRevenue[filterDate || todayIso] || 0)} revenue for {filterDate || todayIso}
            </span>
          )}
          <span className="text-muted-foreground font-mono ml-auto">
            Rates (from Config): weekday <b className="text-cyan-300">{yen(pricing.weekday_price_yen)}</b> · weekend <b className="text-red-300">{yen(pricing.weekend_price_yen)}</b>
          </span>
        </div>

        {walkinLogOpen && (
          <div data-testid="hh-walkin-log" className="rounded-lg border border-fuchsia-500/30 bg-black/30 overflow-x-auto">
            {walkinEntries.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground italic">No walk-ins logged yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-fuchsia-500/20 text-fuchsia-200">
                    <th className="text-left font-heading tracking-widest uppercase py-2 px-3 text-[10px]">Logged At</th>
                    <th className="text-left font-heading tracking-widest uppercase py-2 px-3 text-[10px]">Event Date</th>
                    <th className="text-left font-heading tracking-widest uppercase py-2 px-3 text-[10px]">Type</th>
                    <th className="text-right font-heading tracking-widest uppercase py-2 px-3 text-[10px]">Count</th>
                    <th className="text-right font-heading tracking-widest uppercase py-2 px-3 text-[10px]">Revenue</th>
                    <th className="text-left font-heading tracking-widest uppercase py-2 px-3 text-[10px]">Employee</th>
                    <th className="text-right font-heading tracking-widest uppercase py-2 px-3 text-[10px]">·</th>
                  </tr>
                </thead>
                <tbody>
                  {walkinEntries.map(w => (
                    <tr key={w.id} data-testid={`hh-walkin-row-${w.id}`} className="border-b border-border/40">
                      <td className="py-2 px-3 font-mono text-muted-foreground whitespace-nowrap">
                        {w.added_at ? new Date(w.added_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '—'}
                        {w.edited_at && <span className="ml-1 text-[9px] text-amber-400">(edited)</span>}
                      </td>
                      <td className="py-2 px-3 font-mono text-foreground">{w.event_date}</td>
                      <td className="py-2 px-3">
                        {w.ticket_type === 'weekend' ? (
                          <span className="text-[10px] font-heading tracking-widest uppercase bg-red-900/40 text-red-300 border border-red-500/40 px-1.5 py-0.5 rounded">Weekend</span>
                        ) : w.ticket_type === 'weekday' ? (
                          <span className="text-[10px] font-heading tracking-widest uppercase bg-cyan-900/40 text-cyan-300 border border-cyan-500/40 px-1.5 py-0.5 rounded">Weekday</span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground italic">free</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button type="button" onClick={() => adjustWalkin(w, -1)}
                            disabled={walkinAdjusting === w.id}
                            data-testid={`hh-walkin-minus-${w.id}`}
                            title="Decrease count by 1"
                            className="w-6 h-6 inline-flex items-center justify-center rounded border border-red-500/40 text-red-300 hover:bg-red-900/40 disabled:opacity-50">
                            <Minus size={11} />
                          </button>
                          <span data-testid={`hh-walkin-count-${w.id}`}
                            className={`font-mono min-w-[2.5rem] text-center ${w.count < 0 ? 'text-red-400' : 'text-orange-300'}`}>
                            {walkinAdjusting === w.id ? <Loader2 className="animate-spin inline" size={11} /> : (w.count > 0 ? `+${w.count}` : w.count)}
                          </span>
                          <button type="button" onClick={() => adjustWalkin(w, 1)}
                            disabled={walkinAdjusting === w.id}
                            data-testid={`hh-walkin-plus-${w.id}`}
                            title="Increase count by 1"
                            className="w-6 h-6 inline-flex items-center justify-center rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-900/40 disabled:opacity-50">
                            <Plus size={11} />
                          </button>
                        </div>
                      </td>
                      <td className="py-2 px-3 font-mono text-right text-emerald-300">
                        {w.total_yen ? yen(w.total_yen) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="py-2 px-3 font-sans text-foreground">
                        {w.employee_name ? w.employee_name : <span className="text-muted-foreground italic">—</span>}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <button type="button" onClick={() => openEditWalkin(w)}
                          data-testid={`hh-walkin-edit-${w.id}`}
                          title="Edit this walk-in entry"
                          className="text-cyan-300 hover:bg-cyan-900/40 border border-cyan-500/40 px-2 py-1 mr-1 rounded inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase">
                          <Pencil size={11} /> Edit
                        </button>
                        <button type="button" onClick={() => deleteWalkin(w)}
                          data-testid={`hh-walkin-delete-${w.id}`}
                          title="Delete this walk-in entry"
                          className="text-red-300 hover:bg-red-900/40 border border-red-500/40 px-2 py-1 rounded inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase">
                          <Trash2 size={11} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Daily rollup grid — upcoming dates first */}
      {dailyRollup.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-2" data-testid="hh-daily-rollup">
          <div className="flex items-center gap-2">
            <CalendarDays className="text-orange-500" size={14} />
            <h4 className="font-heading text-xs tracking-widest uppercase">Tickets per Date</h4>
            <span className="text-[10px] text-muted-foreground ml-auto">Today first · upcoming next · past below — click to filter</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {dailyRollup.map(r => {
              const isToday = r.date === todayIso;
              const isPast = r.date < todayIso;
              const isActiveFilter = filterDate === r.date;
              return (
                <button
                  key={r.date}
                  type="button"
                  onClick={() => setFilterDate(isActiveFilter ? '' : r.date)}
                  data-testid={`hh-rollup-${r.date}`}
                  className={`text-left rounded-lg border-2 p-2.5 transition-all ${
                    isActiveFilter
                      ? 'border-orange-500 bg-orange-500/15'
                      : isToday
                        ? 'border-orange-500/80 bg-orange-500/8 hover:border-orange-400'
                        : isPast
                          ? 'border-neutral-800 bg-neutral-950/40 opacity-60 hover:opacity-100'
                          : 'border-border bg-background hover:border-orange-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-mono text-xs ${isToday ? 'text-orange-300 font-bold' : 'text-foreground'}`}>{r.date}</span>
                    {isToday && <span className="text-[8px] font-heading tracking-widest uppercase bg-orange-600 text-black px-1.5 py-0.5 rounded">Today</span>}
                    {isPast && !isToday && <span className="text-[8px] font-heading tracking-widest uppercase bg-neutral-800 text-neutral-400 px-1.5 py-0.5 rounded">Past</span>}
                  </div>
                  <p className="font-heading text-xl mt-1" style={{ color: isToday ? '#fed7aa' : '#fff' }}>{r.tickets + (r.walkins || 0)}</p>
                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5 flex-wrap">
                    <span>{r.orders} order{r.orders === 1 ? '' : 's'}</span>
                    <span>·</span>
                    <span className="text-emerald-400">{yen(r.revenue)}</span>
                    {r.compTickets > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-fuchsia-300">
                        <Gift size={9} />{r.compTickets}
                      </span>
                    )}
                    {r.walkins > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-cyan-300" title={`${r.walkins} manual walk-ins`}>
                        <Plus size={9} />{r.walkins}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-muted-foreground">Filter by date:</label>
        <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} data-testid="hh-filter-date"
          className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm" />
        {filterDate && <button onClick={() => setFilterDate('')} className="text-xs text-orange-500">Clear</button>}
        <button onClick={syncAll} disabled={syncing !== ''} data-testid="hh-orders-sync-all"
          className="inline-flex items-center gap-1 text-xs font-heading tracking-widest uppercase bg-neutral-900 border border-neutral-700 hover:border-orange-500 text-white px-3 py-1.5 rounded-full disabled:opacity-50">
          <RefreshCw size={12} className={syncing === 'all' ? 'animate-spin' : ''} />
          {syncing === 'all' ? 'Syncing…' : 'Sync from Square'}
        </button>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Quick-filter chips */}
      <div className="flex gap-2 flex-wrap" data-testid="hh-status-chips">
        <button onClick={() => { setFilterStatus(''); setCompOnly(false); }} data-testid="hh-status-chip-all"
          className={`font-heading text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border ${(filterStatus === '' && !compOnly) ? 'bg-orange-600 text-black border-orange-500' : 'bg-transparent text-muted-foreground border-border hover:border-orange-500/50'}`}>
          All ({orders.length})
        </button>
        {CHIPS.map(k => (
          <button key={k} onClick={() => { setFilterStatus(filterStatus === k ? '' : k); setCompOnly(false); }} data-testid={`hh-status-chip-${k.toLowerCase()}`}
            className={`font-heading text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border transition-colors ${filterStatus === k ? 'bg-orange-600 text-black border-orange-500' : 'bg-transparent text-muted-foreground border-border hover:border-orange-500/50'} ${(counts[k] || 0) === 0 ? 'opacity-40' : ''}`}>
            {k} ({counts[k] || 0})
          </button>
        ))}
        <button onClick={() => { setCompOnly(!compOnly); setFilterStatus(''); }} data-testid="hh-status-chip-comp"
          className={`inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border transition-colors ${compOnly ? 'bg-fuchsia-600 text-white border-fuchsia-400' : 'bg-transparent text-fuchsia-300 border-fuchsia-500/40 hover:border-fuchsia-400'} ${compCount === 0 ? 'opacity-40' : ''}`}>
          <Gift size={10} /> Comp ({compCount})
        </button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        filtered.length === 0 ? <p className="text-sm text-muted-foreground">No orders {filterStatus ? `with status ${filterStatus}` : 'yet'}.</p> : (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm" data-testid="hh-orders-table">
              <thead className="bg-secondary text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2">Order</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Total</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Buyer</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const used = o.ticket_used_count || 0;
                  const total = o.ticket_total_count || 0;
                  return (
                    <tr key={o.order_id} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-xs">
                        <span>{o.order_num}</span>
                        {o.comp && (
                          <span
                            data-testid={`hh-order-comp-badge-${o.order_id}`}
                            className="ml-2 inline-flex items-center gap-1 text-[9px] font-heading tracking-widest uppercase bg-fuchsia-900/60 text-fuchsia-200 border border-fuchsia-500/50 px-1.5 py-0.5 rounded"
                            title={o.comp_note || 'Admin-issued comp pass'}
                          >
                            <Gift size={9} /> Comp
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {o.flex_pass || !o.event_date ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-fuchsia-900/60 text-fuchsia-200 border border-fuchsia-500/50 px-1.5 py-0.5 rounded">
                            Any Night
                          </span>
                        ) : o.event_date}
                        {o.rescheduled_from && o.rescheduled_from !== (o.event_date || null) && (
                          <div
                            data-testid={`hh-order-moved-${o.order_id}`}
                            title={o.rescheduled_at ? `Rescheduled ${new Date(o.rescheduled_at).toLocaleString()}` : 'Rescheduled'}
                            className="text-[9px] font-heading tracking-widest uppercase text-cyan-400 mt-0.5 inline-flex items-center gap-1"
                          >
                            <CalendarDays size={9} /> moved from {o.rescheduled_from}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 capitalize">{o.ticket_type}</td>
                      <td className="px-3 py-2">{o.quantity}</td>
                      <td className="px-3 py-2">{yen(o.total_yen)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <StatusPill status={o.computed_status || o.ticket_status} />
                        {total > 0 && (o.computed_status === 'PARTIAL' || o.computed_status === 'USED' || o.computed_status === 'VALID') && (
                          <span className="ml-2 text-[10px] font-mono text-muted-foreground">{used}/{total} scanned</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{o.buyer_email || o.buyer_name || '—'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => openCarnival(o.hold_id || o.order_id)}
                          data-testid={`hh-order-print-${o.order_id}`}
                          className="text-neutral-400 hover:text-orange-400 text-xs mr-3 inline-flex items-center gap-1"
                          title="Print carnival-style tickets for cutting out">
                          <Ticket size={11} /> Print
                        </button>
                        <button onClick={() => syncOne(o.order_id)} disabled={syncing !== ''}
                          data-testid={`hh-order-sync-${o.order_id}`}
                          className="text-neutral-400 hover:text-orange-400 text-xs mr-3 inline-flex items-center gap-1 disabled:opacity-50">
                          <RefreshCw size={11} className={syncing === o.order_id ? 'animate-spin' : ''} />
                          {syncing === o.order_id ? 'Syncing' : 'Sync'}
                        </button>
                        <button onClick={() => openReschedule(o)}
                          data-testid={`hh-order-reschedule-${o.order_id}`}
                          className="text-cyan-400 hover:text-cyan-300 text-xs mr-3 inline-flex items-center gap-1"
                          title="Change the date on this order — resets tickets back to VALID">
                          <CalendarDays size={11} /> Change Date
                        </button>
                        <button onClick={() => setConfirmDelete(o)} disabled={deleting === o.order_id}
                          data-testid={`hh-order-delete-${o.order_id}`}
                          className="text-red-400 hover:text-red-300 text-xs mr-3 inline-flex items-center gap-1 disabled:opacity-50"
                          title="Delete this order permanently">
                          <Trash2 size={11} className={deleting === o.order_id ? 'animate-pulse' : ''} />
                          {deleting === o.order_id ? 'Deleting' : 'Delete'}
                        </button>
                        <button onClick={() => openDetail(o.order_id)} data-testid={`hh-order-view-${o.order_id}`} className="text-orange-500 hover:text-orange-400 text-xs">View</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {detail && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-xl w-full max-w-2xl p-6 space-y-4 my-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg">{detail.order_num}</h3>
                <p className="text-xs text-muted-foreground">{detail.event_date} • {detail.ticket_type} • {detail.quantity} admissions • {yen(detail.total_yen)}</p>
                <div className="mt-1 flex items-center gap-2"><StatusPill status={detail.computed_status || detail.ticket_status} /></div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setDetail(null)} className="text-muted-foreground">✕</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(detail.tickets || []).map(t => {
                const role = t.role || 'adult';
                const roleLabel = role === 'kid_0_5' ? 'KID 0–5 · FREE' : role === 'kid_6_9' ? 'KID 6–9' : 'ADULT';
                const roleCls = role === 'kid_0_5'
                  ? 'bg-neutral-700 text-white'
                  : role === 'kid_6_9'
                    ? 'bg-emerald-700 text-white'
                    : 'bg-orange-600 text-black';
                return (
                  <div key={t.token} className="bg-secondary rounded-lg p-3 border border-border text-center">
                    <div className={`font-heading text-[9px] tracking-widest uppercase px-2 py-1 rounded ${roleCls} mb-2`}>{roleLabel}</div>
                    <img src={t.qr_data_uri} alt="qr" className="w-full aspect-square" />
                    <div className="mt-2 text-xs">Admission #{t.admission_number}</div>
                    <div className="mt-1"><StatusPill status={t.status.toUpperCase()} /></div>
                    {t.redeemed_at && <div className="text-[10px] text-muted-foreground mt-1">Used {new Date(t.redeemed_at).toLocaleString()}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[9999] bg-black/85 flex items-center justify-center p-4"
          data-testid="hh-delete-confirm"
          onClick={() => !deleting && setConfirmDelete(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-md bg-neutral-950 border-2 border-red-500 rounded-2xl p-6 space-y-4"
            style={{ boxShadow: '0 0 40px rgba(239,68,68,0.55)' }}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-red-400" size={32} />
              <h3 className="font-heading text-xl tracking-widest uppercase text-red-300">Delete Order?</h3>
            </div>
            <div className="rounded-lg border border-neutral-800 bg-black/60 p-3 text-xs font-mono space-y-0.5">
              <div>Order: <span className="text-orange-300">{confirmDelete.order_num}</span></div>
              <div>Date: {confirmDelete.flex_pass || !confirmDelete.event_date ? 'ANY NIGHT' : confirmDelete.event_date} · {confirmDelete.ticket_type}</div>
              <div>Guests: {confirmDelete.quantity}</div>
              {confirmDelete.buyer_email && <div>Buyer: {confirmDelete.buyer_email}</div>}
              <div>Total: {yen(confirmDelete.total_yen || 0)} {confirmDelete.comp && <span className="text-fuchsia-300 ml-1">· comp</span>}</div>
            </div>
            {!confirmDelete.comp && (confirmDelete.total_yen || 0) > 0 && (
              <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3 text-[11px] text-yellow-200 leading-relaxed">
                <b className="font-heading tracking-widest uppercase">⚠ Paid Order Warning</b><br />
                This customer paid {yen(confirmDelete.total_yen || 0)} via Square. Deleting ONLY removes the local ticket records — it does <b>NOT</b> refund the customer. Issue a refund in Square separately if the money should be returned.
              </div>
            )}
            <p className="text-sm text-neutral-300">
              This permanently removes the order, its tickets, and its hold. This cannot be undone.
            </p>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={!!deleting}
                data-testid="hh-delete-confirm-cancel"
                className="bg-neutral-800 hover:bg-neutral-700 text-white font-heading tracking-widest uppercase text-xs py-3 rounded-full disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={deleteOrder}
                disabled={!!deleting}
                data-testid="hh-delete-confirm-ok"
                className="bg-red-600 hover:bg-red-500 text-white font-heading tracking-widest uppercase text-xs py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="animate-spin" size={12} /> : <Trash2 size={12} />}
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editWalkin && (
        <div
          data-testid="hh-walkin-edit-modal"
          className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !editBusy && setEditWalkin(null)}
        >
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border-2 border-fuchsia-500/60 bg-gradient-to-br from-neutral-950 via-black to-fuchsia-950/40 p-5 shadow-[0_0_40px_rgba(217,70,239,0.35)]">
            <h3 className="font-heading text-sm tracking-widest uppercase text-fuchsia-200 mb-1">
              Edit Walk-in
            </h3>
            <p className="text-[11px] text-muted-foreground mb-4">
              Logged {editWalkin.added_at ? new Date(editWalkin.added_at).toLocaleString() : '—'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Count (negative to subtract)</label>
                <input type="number" value={editCount}
                  onChange={e => setEditCount(e.target.value)}
                  data-testid="hh-walkin-edit-count"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono text-sm outline-none focus:border-fuchsia-500" />
              </div>
              <div>
                <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Event Date</label>
                <input type="date" value={editDate}
                  onChange={e => setEditDate(e.target.value)}
                  data-testid="hh-walkin-edit-date"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono text-sm outline-none focus:border-fuchsia-500" />
              </div>
              <div>
                <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Type (auto-prices from Config)</label>
                <select value={editType}
                  onChange={e => setEditType(e.target.value)}
                  data-testid="hh-walkin-edit-type"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono text-sm outline-none focus:border-fuchsia-500">
                  <option value="">— free (count only, no revenue) —</option>
                  <option value="weekday">Weekday · {yen(pricing.weekday_price_yen)}</option>
                  <option value="weekend">Weekend · {yen(pricing.weekend_price_yen)}</option>
                </select>
              </div>
              <div>
                <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Employee</label>
                <input type="text" value={editEmployee}
                  onChange={e => setEditEmployee(e.target.value)}
                  placeholder="Employee name"
                  maxLength={60}
                  data-testid="hh-walkin-edit-employee"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-fuchsia-500" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button type="button" onClick={() => setEditWalkin(null)}
                disabled={editBusy}
                data-testid="hh-walkin-edit-cancel"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-muted-foreground disabled:opacity-40">
                Cancel
              </button>
              <button type="button" onClick={submitEditWalkin}
                disabled={editBusy || !parseInt(editCount, 10)}
                data-testid="hh-walkin-edit-save"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-40 disabled:cursor-not-allowed text-white inline-flex items-center justify-center gap-1.5">
                {editBusy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                {editBusy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reschedule && (
        <div
          data-testid="hh-reschedule-modal"
          className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !rescheduleBusy && setReschedule(null)}
        >
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border-2 border-cyan-500/60 bg-gradient-to-br from-neutral-950 via-black to-cyan-950/40 p-5 shadow-[0_0_40px_rgba(34,211,238,0.35)] my-auto">
            <h3 className="font-heading text-sm tracking-widest uppercase text-cyan-200 mb-1">
              Change Date · {reschedule.order_num}
            </h3>
            <p className="text-[11px] text-muted-foreground mb-4">
              Current: <b className="text-foreground">{reschedule.flex_pass || !reschedule.event_date ? 'Any Night' : reschedule.event_date}</b> ·
              Resets all {reschedule.quantity} tickets back to <b className="text-emerald-300">VALID</b> (clears scan history).
            </p>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-xs text-fuchsia-200 cursor-pointer">
                <input type="checkbox" checked={rescheduleFlex}
                  onChange={e => setRescheduleFlex(e.target.checked)}
                  data-testid="hh-reschedule-flex"
                  className="accent-fuchsia-500" />
                Convert to <b>Any Night</b> (flex pass — valid any matching event)
              </label>
              {!rescheduleFlex && (
                <div>
                  <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">New Date</label>
                  <select value={rescheduleDate}
                    onChange={e => setRescheduleDate(e.target.value)}
                    data-testid="hh-reschedule-date"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-cyan-500">
                    <option value="">— pick a date —</option>
                    {eventDates.map(d => (
                      <option key={d.date} value={d.date}
                        disabled={!d.is_open}>
                        {d.date}
                        {d.is_past ? ' · past' : ''}
                        {!d.is_open ? ' · closed' : ''}
                        {d.available != null ? ` · ${d.available} left` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-muted-foreground mt-1">Only dates in the Haunted House schedule. Past dates are allowed — useful if a guest actually did show on a different night.</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button type="button" onClick={() => setReschedule(null)}
                disabled={rescheduleBusy}
                data-testid="hh-reschedule-cancel"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-muted-foreground disabled:opacity-40">
                Cancel
              </button>
              <button type="button" onClick={submitReschedule}
                disabled={rescheduleBusy || (!rescheduleFlex && !rescheduleDate)}
                data-testid="hh-reschedule-save"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-black inline-flex items-center justify-center gap-1.5">
                {rescheduleBusy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                {rescheduleBusy ? 'Saving…' : 'Save New Date'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const s = String(status || '').toUpperCase();
  const map = {
    // scan-ready statuses (Orders tab)
    VALID:    'bg-emerald-900/40 text-emerald-400 border-emerald-500/40',
    PARTIAL:  'bg-amber-900/40 text-amber-400 border-amber-500/40',
    USED:     'bg-blue-900/40 text-blue-400 border-blue-500/40',
    EXPIRED:  'bg-neutral-800 text-neutral-400 border-neutral-600',
    REFUNDED: 'bg-red-900/40 text-red-400 border-red-500/40',
    PENDING:  'bg-amber-900/40 text-amber-400 border-amber-500/40',
    // legacy raw ticket_status
    MINTED:   'bg-emerald-900/40 text-emerald-400 border-emerald-500/40',
    VOIDED:   'bg-red-900/40 text-red-400 border-red-500/40',
  };
  return <span className={`inline-block font-heading text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${map[s] || 'bg-neutral-800 text-neutral-400 border-neutral-600'}`}>{s || 'UNKNOWN'}</span>;
}

// =====================================================================
// SQUARE CREDENTIALS FORM
// =====================================================================
function SquareCredentialsForm({ current, onSaved }) {
  const [form, setForm] = useState({
    square_application_id: current.square_app_id || '',
    square_access_token: '',           // never populated — write-only field
    square_location_id: current.square_location_id || '',
    square_webhook_signature_key: '',  // never populated — write-only field
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      // Only send fields the user actually typed (empty string wipes on the server, so skip if untouched)
      const body = {};
      if (form.square_application_id) body.square_application_id = form.square_application_id;
      if (form.square_access_token) body.square_access_token = form.square_access_token;
      if (form.square_location_id) body.square_location_id = form.square_location_id;
      if (form.square_webhook_signature_key) body.square_webhook_signature_key = form.square_webhook_signature_key;
      const r = await axios.put(`${API}/api/admin/hh/square-credentials`, body, { headers: authHeaders() });
      onSaved(r.data);
      setForm({ ...form, square_access_token: '', square_webhook_signature_key: '' });
      toast.success('Square credentials saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const Field = SquareField;  // reference the module-scope component so focus is preserved

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Application ID" value={form.square_application_id} testid="hh-cred-app-id"
               placeholder="sandbox-sq0idb-…"
               onChange={v => setForm({ ...form, square_application_id: v })} />
        <Field label="Location ID" value={form.square_location_id} testid="hh-cred-location-id"
               placeholder="LXXXXXXXXXXXXXX"
               onChange={v => setForm({ ...form, square_location_id: v })} />
        <Field label="Access Token" value={form.square_access_token} testid="hh-cred-access-token"
               placeholder={current.square_access_token_set ? '••••••••••••  (leave blank to keep)' : 'EAAAE…'}
               sensitive alreadySet={current.square_access_token_set}
               onChange={v => setForm({ ...form, square_access_token: v })} />
        <Field label="Webhook Signature Key" value={form.square_webhook_signature_key} testid="hh-cred-sig-key"
               placeholder={current.square_webhook_signature_set ? '••••••••••••  (leave blank to keep)' : 'from Webhook Subscription'}
               sensitive alreadySet={current.square_webhook_signature_set}
               onChange={v => setForm({ ...form, square_webhook_signature_key: v })} />
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="hh-cred-save"
          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-black font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full disabled:opacity-50">
          <Key size={12} />{saving ? 'Saving…' : 'Save Credentials'}
        </button>
      </div>
    </div>
  );
}

// Small button that fetches ONE carnival PNG (admin-authed) and triggers a download.
function PngTicketButton({ ident, orderNum, admission }) {
  const [busy, setBusy] = useState(false);
  const grab = async () => {
    setBusy(true);
    const filename = `OkiEscape-${orderNum}-${String(admission).padStart(3, '0')}.png`;
    try {
      const r = await axios.get(
        `${API}/api/admin/hh/order/${ident}/carnival-png?admission=${admission}`,
        { headers: authHeaders(), responseType: 'blob' },
      );
      const blob = r.data;
      // Try Web Share first (mobile → Photos / Messenger)
      if (typeof navigator !== 'undefined' && navigator.canShare && navigator.share) {
        try {
          const file = new File([blob], filename, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: `Ticket #${admission}`, text: `OKI-ESCAPE Haunted House Ticket #${admission}` });
            return;
          }
        } catch (shareErr) {
          if (shareErr && shareErr.name === 'AbortError') return;
          // fall through to blob download
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error('Could not download ticket PNG');
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={grab}
      disabled={busy}
      data-testid={`comp-png-${admission}`}
      className="aspect-video flex items-center justify-center gap-1 bg-orange-900/40 hover:bg-orange-600 hover:text-black border border-orange-600/50 text-orange-200 font-heading text-[10px] tracking-widest uppercase rounded-md transition-colors disabled:opacity-50"
      title={`Download ticket #${String(admission).padStart(3, '0')} as PNG`}
    >
      {busy ? <Loader2 className="animate-spin" size={10} /> : <>#{String(admission).padStart(3, '0')}</>}
    </button>
  );
}

// =====================================================================
// COMP PASSES — admin issues free guest tickets by email,
// OR flex / promo stubs with no email, no date (for walk-around giveaways).
// =====================================================================
function CompTab() {
  const [cfg, setCfg] = useState(null);
  const [dates, setDates] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [ticketType, setTicketType] = useState('any');
  const [eventDate, setEventDate] = useState('');       // '' = flex / promo
  const [familyOn, setFamilyOn] = useState(false);
  const [useFamilyBreakdown, setUseFamilyBreakdown] = useState(false);
  const [qty, setQty] = useState(1);
  const [adults, setAdults] = useState(1);
  const [kids69, setKids69] = useState(0);
  const [kids05, setKids05] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastIssued, setLastIssued] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/hh/config`).then(r => {
      setCfg(r.data);
      setDates(r.data.dates || []);
      setFamilyOn(!!r.data.family_pricing_enabled);
    }).catch(() => {});
  }, []);

  const availableDates = (dates || []).filter(d => {
    if (!d.is_open || d.is_past || (d.available ?? 0) <= 0) return false;
    if (ticketType === 'any') return true;
    return d.is_weekend === (ticketType === 'weekend');
  });

  // Keep selected date valid when switching weekday/weekend/any
  useEffect(() => {
    if (eventDate && !availableDates.find(d => d.date === eventDate)) {
      setEventDate(''); // reset to flex if the chosen date no longer qualifies
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketType, dates]);

  const isFlex = !eventDate;
  const totalGuests = useFamilyBreakdown ? (adults + kids69 + kids05) : qty;
  const emailValid = !email || email.includes('@');
  const canSubmit = emailValid && totalGuests >= 1 && (!useFamilyBreakdown || adults >= 1 || (kids69 + kids05 === 0));

  const openCarnival = async (ident) => {
    try {
      const r = await axios.get(`${API}/api/admin/hh/order/${ident}/carnival-pdf`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to open carnival PDF — tickets may still be minting');
    }
  };

  const openSheet = async (ident) => {
    try {
      const r = await axios.get(`${API}/api/admin/hh/order/${ident}/sheet-pdf`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to open sheet PDF — tickets may still be minting');
    }
  };

  const submit = async (mode = 'print') => {
    // mode: 'print' = generate + auto-open carnival PDF (recipient is right here)
    //       'sheet' = generate + auto-open the dense 2-inch giveaway sheet PDF
    //       'email' = generate + send email, do NOT auto-open PDF (recipient not here)
    if (!canSubmit) return;
    if (mode === 'email' && !email.trim()) {
      toast.error('Enter an email address to use Save & Email');
      return;
    }
    setBusy(true);
    setLastIssued(null);
    try {
      const payload = {
        ticket_type: ticketType,
        note: note.trim() || undefined,
        send_email: mode === 'email' ? true : false,
      };
      if (email.trim()) payload.recipient_email = email.trim();
      if (name.trim()) payload.recipient_name = name.trim();
      if (eventDate) payload.event_date = eventDate;
      if (mode === 'sheet') {
        // Full-page giveaway sheet — mints 12 (2 cols × 6 rows) ~3.75-inch tickets.
        payload.quantity = 12;
        payload.allow_oversell = true;
      } else if (useFamilyBreakdown) {
        payload.adults = adults;
        payload.kids_6_9 = kids69;
        payload.kids_0_5 = kids05;
      } else {
        payload.quantity = qty;
      }
      const r = await axios.post(`${API}/api/admin/hh/comp`, payload, { headers: authHeaders() });
      setLastIssued({ ...r.data, delivery_mode: mode });
      const flexSuffix = r.data.flex_pass ? ' (flex promo)' : '';

      if (mode === 'email') {
        // Fallback: if auto-email didn't fire (e.g. first-pass), try the resend endpoint
        if (r.data.email_status !== 'auto_sent') {
          try {
            await axios.post(`${API}/api/hh/order/${r.data.hold_id}/email`,
              { email: email.trim() }, { headers: authHeaders() });
          } catch (_) { /* best-effort */ }
        }
        toast.success(`Emailed ${r.data.quantity} pass${r.data.quantity > 1 ? 'es' : ''}${flexSuffix} to ${email.trim()}`);
      } else if (mode === 'sheet') {
        toast.success(`Generated ${r.data.quantity} tickets${flexSuffix} · opening giveaway sheet…`);
        setTimeout(() => openSheet(r.data.hold_id), 400);
      } else {
        toast.success(`Generated ${r.data.quantity} pass${r.data.quantity > 1 ? 'es' : ''}${flexSuffix} · ${r.data.order_num}`);
        // Auto-open the carnival PDF only when printing on-the-spot
        setTimeout(() => openCarnival(r.data.hold_id), 400);
      }

      // Reset partial — keep ticket type / date for batch issuing
      setEmail(''); setName(''); setNote('');
      setQty(1); setAdults(1); setKids69(0); setKids05(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to issue comp passes');
    } finally { setBusy(false); }
  };

  const Stepper = ({ label, value, setValue, min = 0, max = 100 }) => (
    <div className="flex items-center justify-between gap-3 bg-background border border-border rounded-lg px-3 py-2">
      <span className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setValue(Math.max(min, value - 1))}
          className="w-7 h-7 rounded-full bg-neutral-900 border border-neutral-700 hover:border-orange-500 text-white font-heading">−</button>
        <span data-testid={`comp-${label.toLowerCase().replace(/\s+/g, '-')}-value`}
          className="font-mono text-sm w-6 text-center">{value}</span>
        <button type="button" onClick={() => setValue(Math.min(max, value + 1))}
          className="w-7 h-7 rounded-full bg-neutral-900 border border-neutral-700 hover:border-orange-500 text-white font-heading">+</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl space-y-5" data-testid="hh-comp-tab">
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Gift className="text-orange-500" size={18} />
          <h3 className="font-heading text-sm tracking-widest uppercase">Issue Free Guest Passes</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          <b className="text-orange-400">Flex / Promo mode:</b> leave email and date blank to generate
          printable walk-around stubs. <b className="text-orange-400">Specific-guest mode:</b> enter an email
          and date to send a reserved comp — those count toward capacity.
        </p>

        {/* Recipient (OPTIONAL) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Recipient email (optional)" value={email} onChange={setEmail} testid="comp-email" placeholder="leave blank for promo stub" />
          <Field label="Recipient name (optional)" value={name} onChange={setName} testid="comp-name" placeholder="leave blank for promo stub" />
        </div>

        {/* Ticket type (now 3 options including Any) */}
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Pass type</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: 'weekday', label: 'Weekday' },
              { k: 'weekend', label: 'Weekend' },
              { k: 'any',     label: 'Any Date' },
            ].map(({ k, label }) => (
              <button key={k} type="button" onClick={() => setTicketType(k)}
                data-testid={`comp-ticket-type-${k}`}
                className={`font-heading text-xs tracking-widest uppercase py-2.5 rounded-lg border-2 transition-all ${
                  ticketType === k
                    ? 'border-orange-500 bg-orange-500/15 text-orange-200'
                    : 'border-neutral-700 bg-background text-muted-foreground hover:border-orange-500/50'
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Date (OPTIONAL — '' = flex promo) */}
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Event date</label>
          <select value={eventDate} onChange={e => setEventDate(e.target.value)}
            data-testid="comp-date"
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:border-orange-500">
            <option value="">— Flex / Promo (any matching night) —</option>
            {availableDates.map(d => (
              <option key={d.date} value={d.date}>
                {d.date} — {d.available} / {d.capacity} seats left {d.is_weekend ? '(weekend)' : '(weekday)'}
              </option>
            ))}
          </select>
          {isFlex ? (
            <p className="text-[11px] text-fuchsia-300 mt-1">
              🎃 Flex promo stubs — redeemable on <b>any {ticketType === 'any' ? 'open night' : ticketType + ' night'}</b>. These do NOT consume a specific date's capacity.
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1">
              Tied to this specific date — counts toward capacity so you won't oversell.
            </p>
          )}
        </div>

        {/* Family breakdown toggle (only if family pricing is on globally) */}
        {familyOn && (
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={useFamilyBreakdown} onChange={e => setUseFamilyBreakdown(e.target.checked)}
              data-testid="comp-use-family" className="w-4 h-4 accent-orange-500" />
            <span className="text-xs text-foreground">Break down by adults / kids</span>
          </label>
        )}

        {useFamilyBreakdown ? (
          <div className="space-y-2">
            <Stepper label="Adults" value={adults} setValue={setAdults} min={0} max={50} />
            <Stepper label="Kids 6-9" value={kids69} setValue={setKids69} min={0} max={50} />
            <Stepper label="Kids 0-5" value={kids05} setValue={setKids05} min={0} max={50} />
            <p className="text-[11px] text-muted-foreground">Total: <b className="text-orange-400">{totalGuests}</b> guest{totalGuests !== 1 ? 's' : ''}</p>
          </div>
        ) : (
          <Stepper label="Quantity" value={qty} setValue={setQty} min={1} max={100} />
        )}

        {/* Note */}
        <Field label="Note (internal — not shown to recipient)" value={note} onChange={setNote} testid="comp-note" placeholder="e.g. mall promo night · friend of owner · VIP partner" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
          <button
            type="button"
            onClick={() => submit('print')}
            disabled={!canSubmit || busy}
            data-testid="comp-submit-print"
            className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-black font-heading tracking-widest uppercase text-xs py-3 rounded-full"
          >
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Ticket size={13} />}
            Print Now
          </button>
          <button
            type="button"
            onClick={() => submit('sheet')}
            disabled={!canSubmit || busy}
            data-testid="comp-submit-sheet"
            title={eventDate
              ? `Prints 12 big ~3.75-inch bilingual tickets stamped with ${eventDate} — bypasses capacity`
              : "Prints 12 big ~3.75-inch bilingual promo tickets — valid any matching night"}
            className="w-full inline-flex items-center justify-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading tracking-widest uppercase text-xs py-3 rounded-full"
          >
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Ticket size={13} />}
            Print Sheet (12{eventDate ? ` · ${eventDate.slice(5)}` : ''})
          </button>
          <button
            type="button"
            onClick={() => submit('email')}
            disabled={!canSubmit || busy || !email.trim()}
            data-testid="comp-submit-email"
            title={!email.trim() ? 'Enter recipient email to use Save & Email' : ''}
            className="w-full inline-flex items-center justify-center gap-2 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed text-emerald-300 border border-emerald-500/40 hover:border-emerald-400 font-heading tracking-widest uppercase text-xs py-3 rounded-full"
          >
            {busy ? <Loader2 className="animate-spin" size={13} /> : <Mail size={13} />}
            Save & Email
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center -mt-1">
          <b className="text-orange-400">Print Now</b> opens the big carnival PDF (one per page) ·
          <b className="text-fuchsia-300"> Print Sheet (12)</b> fills one page with 12 big ~3.75-inch bilingual tickets ·
          <b className="text-emerald-300"> Save & Email</b> ships them to the recipient
        </p>
      </div>

      {/* Last issued summary */}
      {lastIssued && (
        <div data-testid="comp-last-issued" className="rounded-xl border-2 border-emerald-500/60 bg-emerald-950/30 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-emerald-400" size={18} />
            <h4 className="font-heading text-sm tracking-widest uppercase text-emerald-200">
              {lastIssued.delivery_mode === 'email'
                ? 'Passes Emailed to Recipient'
                : lastIssued.flex_pass ? 'Promo Stubs Ready to Print' : 'Comp Passes Issued'}
            </h4>
          </div>
          <div className="text-xs space-y-0.5 font-mono">
            <div>Order: <span className="text-orange-300">{lastIssued.order_num}</span></div>
            {lastIssued.recipient_email && <div>Recipient: {lastIssued.recipient_email}</div>}
            <div>{lastIssued.quantity} × {lastIssued.ticket_type}{lastIssued.event_date ? ' · ' + lastIssued.event_date : ' · ANY NIGHT'}</div>
            <div>Email: <span className={lastIssued.email_status === 'auto_sent' ? 'text-emerald-300' : 'text-yellow-300'}>{lastIssued.email_status}</span></div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <a href={`${API}/api/hh/order/${lastIssued.hold_id}/pdf`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-orange-300 hover:text-orange-200">
              <Download size={12} /> Email-style PDF
            </a>
            <button type="button" onClick={() => openCarnival(lastIssued.hold_id)}
              data-testid="comp-carnival-print"
              className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-500 text-black font-heading text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full">
              <Ticket size={12} /> Print Carnival PDF
            </button>
            <button type="button" onClick={() => openSheet(lastIssued.hold_id)}
              data-testid="comp-sheet-print"
              title="Dense page — ~2in wide tickets, 48 per page, each with its own QR"
              className="inline-flex items-center gap-1.5 bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-heading text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full">
              <Ticket size={12} /> Print Sheet (2″)
            </button>
          </div>

          {/* Per-ticket PNG gallery — shareable on Facebook / iMessage */}
          <div className="pt-2 border-t border-emerald-800/40">
            <p className="font-heading text-[10px] tracking-widest uppercase text-emerald-300 mb-2 flex items-center gap-1">
              <ImageIcon size={11} /> Share Individual Tickets as PNG
            </p>
            <div className="grid grid-cols-5 sm:grid-cols-8 gap-1.5">
              {Array.from({ length: lastIssued.quantity }, (_, i) => i + 1).map(n => (
                <PngTicketButton key={n} ident={lastIssued.hold_id} orderNum={lastIssued.order_num} admission={n} />
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Tap any ticket to download a 1650×600 PNG. Tap-and-hold on iPhone to save to Photos, or drag into Messenger/iMessage.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4 text-[11px] text-muted-foreground space-y-1">
        <p className="font-heading text-xs tracking-widest uppercase text-foreground mb-1 flex items-center gap-1"><AlertTriangle size={12} />Notes</p>
        <p>• Flex / promo stubs don't need an email or specific date — perfect for walk-around giveaways.</p>
        <p>• Flex promos do <b>NOT</b> consume capacity. Scanner validates by day-type (weekday/weekend/any).</p>
        <p>• Dated comps DO consume capacity so you won't accidentally oversell.</p>
        <p>• Each stub has a unique QR + serial number so you can audit who used what.</p>
      </div>
    </div>
  );
}

// =====================================================================
// SCARER HOURS TRACKER — track volunteer scarer clock-in / clock-out
// =====================================================================
function fmtClock(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Tokyo' });
  } catch { return '—'; }
}
function fmtMinutes(m) {
  m = Math.max(0, Number(m || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h <= 0) return `${r}m`;
  return `${h}h ${r}m`;
}

function ScarersTab() {
  const [scarers, setScarers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [grandTotalMinutes, setGrandTotalMinutes] = useState(0);
  const [date, setDate] = useState(() => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10)); // Tokyo today
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [addShiftFor, setAddShiftFor] = useState(null); // scarer obj or null
  const [shiftForm, setShiftForm] = useState({ date: '', inTime: '', outTime: '', notes: '' });
  const [logMode, setLogMode] = useState('day');
  const [month, setMonth] = useState(() => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 7));
  const [personId, setPersonId] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [renaming, setRenaming] = useState(null);

  const logParams = () => ({
    ...(logMode === 'month' ? { month } : { event_date: date }),
    ...(personId ? { scarer_id: personId } : {}),
  });

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/hh/scarer-shifts/pdf`, {
        headers: authHeaders(), params: logParams(), responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      const who = scarers.find(s => s.id === personId)?.name || 'all';
      a.href = url;
      a.download = `Scarer-Hours-${logMode === 'month' ? month : date}-${who}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Failed to generate PDF'); }
    finally { setPdfBusy(false); }
  };

  const reload = async () => {
    try {
      const [sc, sh] = await Promise.all([
        axios.get(`${API}/api/admin/hh/scarers`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/hh/scarer-shifts`, { headers: authHeaders(), params: logParams() }),
      ]);
      setScarers(sc.data.scarers || []);
      setGrandTotalMinutes(sc.data.grand_total_minutes || 0);
      setShifts(sh.data.shifts || []);
      setTotalMinutes(sh.data.total_minutes || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [date, logMode, month, personId]);
  // Keep live minutes ticking for open shifts
  useEffect(() => {
    const anyOpen = scarers.some(s => s.clocked_in) || shifts.some(s => s.open);
    if (!anyOpen) return;
    const id = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, [scarers, shifts]);
  useEffect(() => { if (tick > 0) reload(); /* eslint-disable-next-line */ }, [tick]);

  const addScarer = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/hh/scarers`, { name }, { headers: authHeaders() });
      setNewName('');
      await reload();
      toast.success(`Added ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add scarer');
    } finally { setBusy(false); }
  };

  const removeScarer = async (s) => {
    if (!window.confirm(`Remove ${s.name}? Past shifts are kept.`)) return;
    try {
      await axios.delete(`${API}/api/admin/hh/scarers/${s.id}`, { headers: authHeaders() });
      await reload();
    } catch { toast.error('Could not remove'); }
  };

  const clockIn = async (s) => {
    try {
      await axios.post(`${API}/api/admin/hh/scarers/${s.id}/clock-in`, {}, { headers: authHeaders() });
      toast.success(`${s.name} clocked in`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clock in');
    }
  };

  const clockOut = async (s) => {
    try {
      const r = await axios.post(`${API}/api/admin/hh/scarers/${s.id}/clock-out`, {}, { headers: authHeaders() });
      toast.success(`${s.name} clocked out — ${fmtMinutes(r.data.minutes)}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clock out');
    }
  };

  const openAddShift = (s) => {
    const today = new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
    setShiftForm({ date: today, inTime: '19:00', outTime: '22:00', notes: '' });
    setEditingShift(null);
    setAddShiftFor(s);
  };

  const tokyoParts = (iso) => {
    if (!iso) return { date: '', time: '' };
    const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
    const t = new Date(d.getTime() + 9 * 3600_000).toISOString();
    return { date: t.slice(0, 10), time: t.slice(11, 16) };
  };

  const openEditShift = (sh) => {
    const ci = tokyoParts(sh.clock_in);
    const co = tokyoParts(sh.clock_out);
    setShiftForm({ date: ci.date, inTime: ci.time, outTime: co.time, notes: sh.notes || '' });
    setEditingShift(sh);
    setAddShiftFor({ id: sh.scarer_id, name: sh.scarer_name });
  };

  const saveRename = async () => {
    const name = renaming?.name.trim();
    if (!name) return;
    try {
      await axios.patch(`${API}/api/admin/hh/scarers/${renaming.id}`, { name }, { headers: authHeaders() });
      toast.success(`Renamed to ${name}`);
      setRenaming(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not rename');
    }
  };

  const submitAddShift = async () => {
    if (!addShiftFor) return;
    const { date: d, inTime, outTime, notes } = shiftForm;
    const outOptional = editingShift?.open;
    if (!d || !inTime || (!outTime && !outOptional)) {
      toast.error('Date, clock-in and clock-out are all required');
      return;
    }
    // Build local Tokyo datetimes and convert to UTC ISO for backend
    const buildIso = (dateStr, timeStr) => {
      const [y, m, day] = dateStr.split('-').map(Number);
      const [hh, mm] = timeStr.split(':').map(Number);
      // Treat input as Asia/Tokyo (UTC+9). Local -> UTC by subtracting 9h.
      return new Date(Date.UTC(y, m - 1, day, hh - 9, mm, 0)).toISOString();
    };
    try {
      const ciIso = buildIso(d, inTime);
      let coIso = outTime ? buildIso(d, outTime) : null;
      // Shift that crosses midnight: push clock-out to next day
      if (coIso && new Date(coIso) <= new Date(ciIso)) {
        coIso = new Date(new Date(coIso).getTime() + 86400_000).toISOString();
      }
      setBusy(true);
      if (editingShift) {
        const r = await axios.patch(`${API}/api/admin/hh/scarer-shifts/${editingShift.id}`, {
          clock_in: ciIso,
          ...(coIso ? { clock_out: coIso } : {}),
          notes: notes.trim(),
        }, { headers: authHeaders() });
        toast.success(`Shift updated · ${fmtMinutes(r.data.minutes)}`);
        setAddShiftFor(null);
        setEditingShift(null);
        await reload();
        return;
      }
      const r = await axios.post(`${API}/api/admin/hh/scarer-shifts`, {
        scarer_id: addShiftFor.id,
        clock_in: ciIso,
        clock_out: coIso,
        notes: notes.trim() || undefined,
      }, { headers: authHeaders() });
      toast.success(`Added ${fmtMinutes(r.data.minutes)} for ${addShiftFor.name}`);
      setAddShiftFor(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add shift');
    } finally { setBusy(false); }
  };

  const deleteShift = async (sh) => {
    if (!window.confirm(`Delete this shift for ${sh.scarer_name}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/hh/scarer-shifts/${sh.id}`, { headers: authHeaders() });
      await reload();
    } catch { toast.error('Could not delete'); }
  };

  // Per-scarer total minutes for the viewed date
  const totalsByScarer = useMemo(() => {
    const m = {};
    shifts.forEach(sh => {
      m[sh.scarer_name] = (m[sh.scarer_name] || 0) + (sh.minutes || 0);
    });
    return m;
  }, [shifts]);

  const today = new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
  const anyoneLive = scarers.some(s => s.clocked_in);

  return (
    <div className="max-w-5xl space-y-5" data-testid="hh-scarers-tab">
      {/* Header */}
      <div className="rounded-xl border-2 border-orange-500/40 bg-gradient-to-br from-orange-950/40 via-black to-neutral-950 p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Clock className="text-orange-400" size={22} />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-sm tracking-widest uppercase text-orange-200">Guest Scarer Hours</h3>
            <p className="font-sans text-xs text-muted-foreground mt-1">
              Clock volunteers in when they show up to help scare, clock them out when they leave. Minutes compute automatically.
            </p>
          </div>
          {anyoneLive && (
            <span className="font-heading text-[10px] tracking-widest uppercase bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {scarers.filter(s => s.clocked_in).length} ON CLOCK
            </span>
          )}
          <span data-testid="scarer-grand-total" className="font-heading text-[10px] tracking-widest uppercase bg-orange-500/20 border border-orange-500/40 text-orange-200 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
            <Clock size={11} /> Guests total: <b className="text-orange-100">{fmtMinutes(grandTotalMinutes)}</b>
          </span>
        </div>
      </div>

      {/* Scarer roster */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h4 className="font-heading text-xs tracking-widest uppercase flex items-center gap-2">
            <UserPlus size={14} className="text-orange-400" /> Scarers
          </h4>
          <div className="flex items-center gap-2">
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addScarer()}
              placeholder="Add new scarer (e.g. Mike)"
              data-testid="scarer-new-name"
              className="bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-orange-500 w-56"
            />
            <button type="button" onClick={addScarer} disabled={busy || !newName.trim()}
              data-testid="scarer-add-btn"
              className="font-heading text-[11px] tracking-widest uppercase bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-black px-3 py-2 rounded-lg inline-flex items-center gap-1">
              <Plus size={12} /> Add
            </button>
          </div>
        </div>

        {scarers.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No scarers yet — add one above to start tracking.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {scarers.map(s => (
              <div key={s.id} data-testid={`scarer-card-${s.id}`}
                className={`rounded-xl border-2 p-4 transition-all ${s.clocked_in ? 'border-emerald-500/60 bg-emerald-950/30' : 'border-neutral-700 bg-neutral-900/60'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {renaming?.id === s.id ? (
                      <div className="flex items-center gap-1">
                        <input autoFocus value={renaming.name}
                          onChange={e => setRenaming(r => ({ ...r, name: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null); }}
                          data-testid={`scarer-rename-input-${s.id}`}
                          className="w-28 bg-background border border-orange-500/60 rounded px-2 py-1 text-xs outline-none" />
                        <button type="button" onClick={saveRename} data-testid={`scarer-rename-save-${s.id}`}
                          title="Save name" className="text-emerald-300 hover:text-emerald-200 p-1"><Save size={12} /></button>
                        <button type="button" onClick={() => setRenaming(null)} data-testid={`scarer-rename-cancel-${s.id}`}
                          title="Cancel" className="text-muted-foreground hover:text-foreground p-1 text-xs">✕</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="font-heading text-sm uppercase tracking-wide text-foreground truncate">{s.name}</span>
                        <button type="button" onClick={() => setRenaming({ id: s.id, name: s.name })}
                          data-testid={`scarer-rename-${s.id}`}
                          title="Rename scarer"
                          className="text-cyan-300 hover:text-cyan-200 p-0.5 rounded"><Pencil size={11} /></button>
                      </div>
                    )}
                    <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
                      {s.clocked_in
                        ? <>IN since <span className="text-emerald-300">{fmtClock(s.clock_in_at)}</span></>
                        : <>Off clock</>}
                    </div>
                    <div className="font-mono text-[11px] mt-1">
                      Today: <b className={s.today_minutes > 0 ? 'text-orange-300' : 'text-muted-foreground'}>{fmtMinutes(s.today_minutes)}</b>
                    </div>
                    <div className="font-mono text-[11px]">
                      Total: <b className={s.lifetime_minutes > 0 ? 'text-fuchsia-300' : 'text-muted-foreground'}>{fmtMinutes(s.lifetime_minutes)}</b>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeScarer(s)}
                    data-testid={`scarer-delete-${s.id}`}
                    title="Remove scarer (past shifts are kept)"
                    className="text-red-400 hover:text-red-300 p-1 rounded">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="mt-3 space-y-1.5">
                  {s.clocked_in ? (
                    <button type="button" onClick={() => clockOut(s)}
                      data-testid={`scarer-clock-out-${s.id}`}
                      className="w-full font-heading text-[11px] tracking-widest uppercase bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded-lg inline-flex items-center justify-center gap-1.5">
                      <StopSquare size={12} /> Clock Out
                    </button>
                  ) : (
                    <button type="button" onClick={() => clockIn(s)}
                      data-testid={`scarer-clock-in-${s.id}`}
                      className="w-full font-heading text-[11px] tracking-widest uppercase bg-emerald-600 hover:bg-emerald-500 text-black px-3 py-2 rounded-lg inline-flex items-center justify-center gap-1.5">
                      <Play size={12} /> Clock In
                    </button>
                  )}
                  <button type="button" onClick={() => openAddShift(s)}
                    data-testid={`scarer-add-shift-${s.id}`}
                    title="Admin — manually add a shift you forgot to clock in"
                    className="w-full font-heading text-[10px] tracking-widest uppercase bg-neutral-800 hover:bg-neutral-700 text-fuchsia-300 border border-fuchsia-500/30 hover:border-fuchsia-400 px-3 py-1.5 rounded-lg inline-flex items-center justify-center gap-1.5">
                    <Plus size={11} /> Add Past Shift
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shift log */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h4 className="font-heading text-xs tracking-widest uppercase flex items-center gap-2">
            <ClipboardList size={14} className="text-orange-400" /> Shift Log
          </h4>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-full border border-orange-500/40 overflow-hidden" data-testid="scarer-log-mode">
              {['day', 'month'].map(m => (
                <button key={m} type="button" onClick={() => setLogMode(m)}
                  data-testid={`scarer-log-mode-${m}`}
                  className={`font-heading text-[10px] tracking-widest uppercase px-3 py-1.5 transition-colors ${logMode === m ? 'bg-orange-500/30 text-orange-100' : 'text-muted-foreground hover:text-orange-200'}`}>
                  {m}
                </button>
              ))}
            </div>
            {logMode === 'day' ? (
              <>
                <input type="date" value={date} onChange={e => setDate(e.target.value)}
                  data-testid="scarer-log-date"
                  className="bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs font-mono outline-none focus:border-orange-500" />
                {date !== today && (
                  <button type="button" onClick={() => setDate(today)}
                    data-testid="scarer-log-today"
                    className="font-heading text-[10px] tracking-widest uppercase text-orange-300 hover:text-orange-200 underline">Today</button>
                )}
              </>
            ) : (
              <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                data-testid="scarer-log-month"
                className="bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs font-mono outline-none focus:border-orange-500" />
            )}
            <select value={personId} onChange={e => setPersonId(e.target.value)}
              data-testid="scarer-log-person"
              className="bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-orange-500">
              <option value="">Everyone</option>
              {scarers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button type="button" onClick={downloadPdf} disabled={pdfBusy}
              data-testid="scarer-log-pdf"
              className="inline-flex items-center gap-1 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white font-heading text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full">
              {pdfBusy ? <Loader2 className="animate-spin" size={11} /> : <Download size={11} />} PDF
            </button>
          </div>
        </div>

        {/* Totals */}
        <div className="flex items-center flex-wrap gap-2 text-[11px]">
          <span data-testid="scarer-log-total" className="font-heading tracking-widest uppercase bg-orange-500/15 border border-orange-500/40 text-orange-200 px-2.5 py-1 rounded-full">
            {logMode === 'month' ? 'Month Total' : 'Total'}: <b className="text-orange-100">{fmtMinutes(totalMinutes)}</b>
          </span>
          {Object.entries(totalsByScarer).map(([name, mins]) => (
            <span key={name} data-testid={`scarer-log-person-total-${name}`} className="font-mono bg-neutral-800 border border-neutral-700 text-neutral-300 px-2 py-0.5 rounded">
              {name}: {fmtMinutes(mins)}
            </span>
          ))}
        </div>

        {shifts.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="scarer-log-empty">No shifts recorded for {logMode === 'month' ? month : date}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  {logMode === 'month' && <th className="text-left font-heading tracking-widest uppercase py-2 text-[10px]">Date</th>}
                  <th className="text-left font-heading tracking-widest uppercase py-2 text-[10px]">Scarer</th>
                  <th className="text-left font-heading tracking-widest uppercase py-2 text-[10px]">In</th>
                  <th className="text-left font-heading tracking-widest uppercase py-2 text-[10px]">Out</th>
                  <th className="text-left font-heading tracking-widest uppercase py-2 text-[10px]">Duration</th>
                  <th className="text-right font-heading tracking-widest uppercase py-2 text-[10px]">·</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map(sh => (
                  <tr key={sh.id} data-testid={`scarer-shift-row-${sh.id}`} className="border-b border-border/60">
                    {logMode === 'month' && <td className="py-2 font-mono text-muted-foreground">{sh.event_date}</td>}
                    <td className="py-2 font-mono text-foreground">{sh.scarer_name}</td>
                    <td className="py-2 font-mono text-muted-foreground">{fmtClock(sh.clock_in)}</td>
                    <td className="py-2 font-mono">
                      {sh.open ? (
                        <span className="inline-flex items-center gap-1 text-emerald-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> on clock
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{fmtClock(sh.clock_out)}</span>
                      )}
                    </td>
                    <td className={`py-2 font-mono ${sh.open ? 'text-emerald-300' : 'text-orange-300'}`}>
                      {fmtMinutes(sh.minutes)}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button type="button" onClick={() => openEditShift(sh)}
                        data-testid={`scarer-shift-edit-${sh.id}`}
                        title="Edit this shift"
                        className="text-cyan-300 hover:bg-cyan-900/40 border border-cyan-500/40 px-2 py-1 mr-1 rounded inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase">
                        <Pencil size={11} /> Edit
                      </button>
                      <button type="button" onClick={() => deleteShift(sh)}
                        data-testid={`scarer-shift-delete-${sh.id}`}
                        title="Delete this shift"
                        className="text-red-400 hover:text-red-300 p-1 rounded">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addShiftFor && (
        <div data-testid="scarer-add-shift-modal"
          className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAddShiftFor(null)}>
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border-2 border-fuchsia-500/60 bg-gradient-to-br from-neutral-950 via-black to-fuchsia-950/40 p-5 shadow-[0_0_40px_rgba(217,70,239,0.35)]">
            <h3 className="font-heading text-sm tracking-widest uppercase text-fuchsia-200 mb-1" data-testid="scarer-shift-modal-title">
              {editingShift ? 'Edit Shift' : 'Add Past Shift'} · {addShiftFor.name}
            </h3>
            <p className="text-[11px] text-muted-foreground mb-4">
              {editingShift ? 'Fix the date, times or notes.' : 'Log a shift you forgot to clock.'} Times are Asia/Tokyo. Minutes calculate automatically.
              {editingShift?.open && ' Leave Clock Out blank to keep them on the clock.'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Date</label>
                <input type="date" value={shiftForm.date}
                  onChange={e => setShiftForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="scarer-add-shift-date"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-fuchsia-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Clock In</label>
                  <input type="time" value={shiftForm.inTime}
                    onChange={e => setShiftForm(f => ({ ...f, inTime: e.target.value }))}
                    data-testid="scarer-add-shift-in"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-fuchsia-500" />
                </div>
                <div>
                  <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Clock Out</label>
                  <input type="time" value={shiftForm.outTime}
                    onChange={e => setShiftForm(f => ({ ...f, outTime: e.target.value }))}
                    data-testid="scarer-add-shift-out"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-fuchsia-500" />
                </div>
              </div>
              <div>
                <label className="block font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Notes (optional)</label>
                <input value={shiftForm.notes}
                  onChange={e => setShiftForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. covered the clown room"
                  data-testid="scarer-add-shift-notes"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-sans outline-none focus:border-fuchsia-500" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button type="button" onClick={() => setAddShiftFor(null)}
                data-testid="scarer-add-shift-cancel"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-muted-foreground">
                Cancel
              </button>
              <button type="button" onClick={submitAddShift}
                disabled={busy || !shiftForm.date || !shiftForm.inTime || (!shiftForm.outTime && !editingShift?.open)}
                data-testid="scarer-add-shift-save"
                className="font-heading text-[11px] tracking-widest uppercase py-2.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 disabled:opacity-40 disabled:cursor-not-allowed text-white inline-flex items-center justify-center gap-1.5">
                {busy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save Shift
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// =====================================================================
function ScannerTab() {
  const [pin, setPin] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/admin/hh/settings`, { headers: authHeaders() })
      .then(r => setPin(r.data.door_pin)).catch(() => {});
  }, []);
  const url = `${window.location.origin}/scan`;
  return (
    <div className="max-w-lg space-y-4">
      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <h3 className="font-heading text-sm tracking-widest uppercase">Door Scanner</h3>
        <p className="text-xs text-muted-foreground">Open this page on any phone, unlock with the door PIN (or admin login), and scan customer QR codes to check them in.</p>
        <div className="rounded-lg bg-background border border-border p-3 text-sm font-mono break-all" data-testid="hh-scanner-url">{url}</div>
        <div className="flex items-center gap-3">
          <a href="/scan" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-black font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full">
            <ExternalLink size={12} />Open Scanner
          </a>
          {pin && <div className="text-xs">Door PIN: <code className="text-orange-400">{pin}</code></div>}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-5 text-xs text-muted-foreground space-y-2">
        <p className="font-heading text-xs tracking-widest uppercase text-foreground">Status legend</p>
        <ul className="space-y-1.5">
          <li><StatusPill status="VALID" /> ticket is good — automatically marked used after scan</li>
          <li><StatusPill status="USED" /> already scanned in — shows when it was used</li>
          <li><StatusPill status="REFUNDED" /> voided by a refund</li>
          <li><StatusPill status="WRONG_DATE" /> ticket is for a different date</li>
          <li><StatusPill status="INVALID" /> unknown QR token</li>
        </ul>
      </div>
    </div>
  );
}

// =====================================================================
// PUBLIC QR — Copy-paste link + downloadable QR poster for the ticket flow
// =====================================================================
function ShareTab() {
  const previewOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const [publicUrl, setPublicUrl] = useState('');   // saved value
  const [draft, setDraft] = useState('');           // input field
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/hh/settings`, { headers: authHeaders() })
      .then(r => { const u = r.data?.public_site_url || ''; setPublicUrl(u); setDraft(u); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/hh/settings`, { public_site_url: draft.trim() }, { headers: authHeaders() });
      const u = r.data?.public_site_url || '';
      setPublicUrl(u); setDraft(u);
      toast.success(u ? 'Live site URL saved — links updated' : 'Cleared — using this preview URL');
    } catch {
      toast.error('Could not save');
    } finally { setSaving(false); }
  };

  const base = publicUrl || previewOrigin;
  const usingLive = !!publicUrl;
  const linkQuery = `${base}/?book=haunted`;
  const linkVanity = `${base}/haunted`;
  const qrSrc = `${API}/api/hh/qr-poster?url=${encodeURIComponent(linkQuery)}&size=20`;

  const copy = async (text, label = 'Link') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed — long-press the link to copy manually');
    }
  };

  const download = async () => {
    try {
      const res = await fetch(qrSrc);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'haunted-house-ticket-qr.png';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="hh-share-tab">
      {/* Live site URL setting */}
      <div className="rounded-xl border border-orange-500/40 bg-orange-500/5 p-5 space-y-3">
        <div>
          <h3 className="font-heading text-sm tracking-widest uppercase text-orange-400">Live Site URL</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Set your production domain here (e.g. <code className="text-orange-300">https://okiescape.com</code>).
            All links + QR codes below will use it instead of this admin/preview URL.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="hh-share-public-url-input"
            type="url"
            placeholder="https://okiescape.com"
            value={draft}
            disabled={loading || saving}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-orange-500"
          />
          <button
            data-testid="hh-share-save-public-url"
            onClick={save}
            disabled={loading || saving || draft.trim() === publicUrl}
            className="inline-flex items-center justify-center gap-2 text-xs font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-black px-4 py-2 rounded-full">
            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div className="text-[11px]">
          {usingLive
            ? <span className="text-green-400">✓ Using live URL: <code>{publicUrl}</code></span>
            : <span className="text-yellow-400">⚠ Not set — falling back to <code>{previewOrigin}</code> (preview URL, only works inside Emergent)</span>}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div>
          <h3 className="font-heading text-sm tracking-widest uppercase text-orange-400">Public Ticket QR</h3>
          <p className="text-xs text-muted-foreground mt-1">Share this link or print the QR. Anyone who scans it lands on the site with the Haunted House ticket popup already open.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-background p-3 space-y-2">
            <div className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Short link</div>
            <div className="text-sm font-mono break-all" data-testid="hh-share-vanity-link">{linkVanity}</div>
            <button data-testid="hh-share-copy-vanity" onClick={() => copy(linkVanity, 'Short link')}
              className="inline-flex items-center gap-2 text-xs font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 text-black px-3 py-1.5 rounded-full">
              <Copy size={12} /> Copy
            </button>
          </div>

          <div className="rounded-lg border border-border bg-background p-3 space-y-2">
            <div className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">Full link (works everywhere)</div>
            <div className="text-sm font-mono break-all" data-testid="hh-share-query-link">{linkQuery}</div>
            <button data-testid="hh-share-copy-query" onClick={() => copy(linkQuery, 'Link')}
              className="inline-flex items-center gap-2 text-xs font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 text-black px-3 py-1.5 rounded-full">
              <Copy size={12} /> Copy
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading text-sm tracking-widest uppercase">Poster QR (PNG)</h3>
          <button data-testid="hh-share-download-qr" onClick={download}
            className="inline-flex items-center gap-2 text-xs font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 text-black px-4 py-2 rounded-full">
            <Download size={12} /> Download PNG
          </button>
        </div>
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="rounded-lg bg-white p-3 inline-block">
            <img src={qrSrc} alt="Haunted House Ticket QR" data-testid="hh-share-qr-image"
              className="block w-56 h-56 object-contain" />
          </div>
          <div className="text-xs text-muted-foreground space-y-2 flex-1">
            <p><span className="font-heading uppercase tracking-widest text-foreground">Where to use:</span> flyers, entrance signage, receipts, Instagram stories, LINE broadcasts.</p>
            <p>Scanning it opens the site and pops the Haunted House ticket flow immediately — no menu-hunting.</p>
            <p className="text-[11px] text-neutral-500">High error-correction QR (H) — survives ~30% damage. Print at 3cm or larger for best scan distance.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

